import type { Catalog } from "@/lib/i18n/translate";
import type { MessageKey } from "@/lib/i18n/messages.de";

/**
 * Swiss French UI texts (formal "vous"), machine translated from German and open for
 * corrections. Amounts and dates follow Swiss conventions (see lib/locale). Tax terms come
 * from the country profile.
 */
export const fr: Catalog<MessageKey> = {
  "app.name": "Rhinvoice",
  "app.skipToContent": "Aller au contenu",
  "app.loading": "Chargement …",
  "app.pageTitle": "{title} · Rhinvoice",

  "nav.main": "Navigation principale",
  "nav.dashboard": "Vue d’ensemble",
  "nav.documents": "Documents",
  "nav.invoices": "Factures",
  "nav.quotes": "Offres",
  "nav.customers": "Clients",
  "nav.articles": "Articles",
  "nav.settings": "Paramètres",
  "nav.newInvoice": "Nouvelle facture",
  "nav.more": "Plus",
  "nav.openMenu": "Ouvrir le menu",
  "nav.collapse": "Réduire la barre latérale",
  "nav.expand": "Déployer la barre latérale",

  "topbar.search": "Rechercher …",
  "topbar.help": "Aide et raccourcis clavier",
  "topbar.account": "Compte",
  "topbar.logout": "Se déconnecter",

  "theme.label": "Thème",
  "theme.light": "Clair",
  "theme.dark": "Sombre",
  "theme.system": "Système",
  "language.label": "Langue",
  "language.machineTranslated": "Traduction automatique – vos remarques sont les bienvenues",
  "demo.title": "Démo",
  "demo.loginHint":
    "Essayez Rhinvoice avec des données d’exemple. Tous les visiteurs partagent cette démo ; elle est réinitialisée toutes les {minutes} minutes et aucun e-mail n’est envoyé.",
  "demo.locked":
    "Non disponible dans la démo publique : tous les visiteurs y sont connectés avec le même accès et aucun e-mail n’est envoyé.",
  "demo.signIn": "Ouvrir la démo",
  "demo.otherCountries": "Aussi en démo :",
  "demo.banner":
    "Données et comptes d’exemple, merci de ne rien virer · réinitialisées toutes les {minutes} minutes, aucun e-mail",
  "demo.countryDemo": "Démo {country}",
  "about.title": "À propos de Rhinvoice",
  "about.version": "Version {version}",
  "about.description":
    "Logiciel de facturation open source pour la Suisse, le Liechtenstein, l’Allemagne et l’Autriche.",
  "about.license": "Licence : GNU Affero General Public License v3",
  "about.source": "Code source",
  "about.reportBug": "Signaler un problème",
  "support.title": "Signaler un problème",
  "support.description":
    "Rhinvoice n’envoie rien de lui-même. Vous copiez les informations et décidez où elles vont.",
  "support.copyStep":
    "1. Copier le journal. Il peut contenir des noms de clients : parcourez-le avant de l’envoyer.",
  "support.copy": "Copier le journal",
  "support.copied": "Journal copié",
  "support.copyFailed":
    "La copie n’a pas fonctionné. Sélectionnez le texte ci-dessous et copiez-le vous-même.",
  "support.serverStep":
    "1. Le journal se trouve sur le serveur. Exécutez-y cette commande et copiez le résultat :",
  "support.sendStep": "2. Signaler sur GitHub ou envoyer par e-mail à {email}.",
  "support.github": "Signaler sur GitHub",
  "about.changelog": "Nouveautés",
  "about.noAdvice": "Pas de conseil fiscal, taux de TVA sans garantie.",
  "about.machineTranslated":
    "Cette traduction a été réalisée automatiquement. Vos corrections sont les bienvenues :",
  "about.reportTranslation": "Signaler une erreur de traduction",

  "updates.title": "Mise à jour",
  "updates.newVersion": "Nouvelle version {version}",
  "updates.notes": "Quoi de neuf ?",
  "updates.install": "Mettre à jour maintenant",
  "updates.installHint":
    "Dure environ une demi-minute. Vos données sont sauvegardées au préalable.",
  "updates.installing": "Téléchargement …",
  "updates.restarting": "Rhinvoice redémarre …",
  "updates.commandEnv": "Définir dans le fichier .env :",
  "updates.commandEnvIfPinned": "Seulement si .env fixe une version, y définir :",
  "updates.commandOnly": "Exécuter sur le serveur :",
  "updates.commandRun": "Puis exécuter sur le serveur :",
  "updates.setupTitle": "{version} nécessite un nouveau programme d’installation",
  "updates.setupHint": "Installez-le une fois, vos données sont conservées.",
  "updates.setupDownload": "Télécharger le programme d’installation",
  "updates.failedTitle": "La mise à jour vers {version} n’a pas fonctionné",
  "updates.failedHint": "Rhinvoice {current} fonctionne à nouveau, vos données sont inchangées.",
  "updates.upToDate": "Rhinvoice est à jour.",
  "updates.notChecked": "La disponibilité d’une nouvelle version n’a pas encore été vérifiée.",
  "updates.check": "Rechercher des mises à jour",
  "updates.checking": "Vérification …",
  "updates.setupDownloading": "Téléchargement … {received} sur {total} Mo",
  "updates.setupReady": "Téléchargé et vérifié.",
  "updates.setupInstall": "Installer",
  "updates.setupFailed": "Le téléchargement n’a pas abouti.",
  "updates.setupRetry": "Réessayer",
  "updates.setupByHand": "Télécharger vous-même le programme d’installation",
  "updates.closeTitle": "Fermer Rhinvoice pour la mise à jour ?",
  "updates.closeSetup":
    "Rhinvoice se ferme, installe {version} puis redémarre tout seul. Cela prend environ une minute, vos données sont conservées.",
  "updates.closeRestart":
    "Rhinvoice redémarre pour {version}. Cela prend environ une demi-minute, vos données sont sauvegardées auparavant.",
  "updates.closeSaveFirst": "Enregistrez d’abord tout ce sur quoi vous travaillez.",
  "updates.closeUnsaved":
    "Des modifications ne sont pas enregistrées. Choisissez « Plus tard », enregistrez-les puis relancez la mise à jour.",
  "updates.closeConfirm": "Fermer et mettre à jour",
  "updates.closeAnyway": "Fermer quand même",
  "updates.later": "Plus tard",
  "updates.closing": "Rhinvoice se ferme pour la mise à jour …",
  "about.madeBy": "made by {maker}",
  "about.legalNotice": "Mentions légales",
  "about.privacy": "Protection des données",
  "about.openSource": "Open source",

  "palette.title": "Palette de commandes",
  "palette.placeholder": "Saisir une commande ou un terme de recherche …",
  "palette.empty": "Aucun résultat.",
  "palette.actions": "Actions",
  "palette.navigation": "Navigation",
  "palette.toggleTheme": "Changer de thème",
  "palette.shortcuts": "Afficher les raccourcis clavier",

  "shortcuts.title": "Raccourcis clavier",
  "shortcuts.description":
    "Les raccourcis à une touche ne sont actifs que si aucun champ de saisie n’a le focus.",
  "shortcuts.groupGeneral": "Général",
  "shortcuts.groupNavigation": "Navigation",
  "shortcuts.palette": "Ouvrir la palette de commandes",
  "shortcuts.search": "Activer la recherche",
  "shortcuts.newInvoice": "Nouvelle facture",
  "shortcuts.save": "Enregistrer",
  "shortcuts.help": "Cet aperçu",
  "shortcuts.close": "Fermer la boîte de dialogue",
  "shortcuts.goDashboard": "Aller à la vue d’ensemble",
  "shortcuts.goInvoices": "Aller aux factures",
  "shortcuts.goCustomers": "Aller aux clients",
  "shortcuts.goArticles": "Aller aux articles",
  "shortcuts.goSettings": "Aller aux paramètres",
  "shortcuts.then": "puis",

  "auth.loginTitle": "Se connecter",
  "auth.loginDescription": "Connectez-vous avec votre adresse e-mail.",
  "auth.email": "Adresse e-mail",
  "auth.password": "Mot de passe",
  "auth.totpCode": "Code de confirmation",
  "auth.totpHint": "Code à six chiffres de votre application d’authentification.",
  "auth.submit": "Se connecter",
  "auth.submitting": "Connexion …",
  "auth.forgotPassword": "Mot de passe oublié ?",
  "auth.reset.title": "Mot de passe oublié",
  "auth.reset.description":
    "Vous définissez un nouveau mot de passe. Ensuite, toutes les sessions ouvertes avec l’ancien sont fermées.",
  "auth.reset.administrator":
    "Un nouveau mot de passe est défini par la personne qui gère le serveur. Pour une installation avec Docker, dans le dossier de Rhinvoice :",
  "auth.reset.whichAddress": "Quelle adresse e-mail ? Cette commande affiche tous les comptes :",
  "auth.reset.file":
    "Rhinvoice dépose un code à usage unique dans son dossier de données sur cet ordinateur et ouvre le fichier. Celui-ci contient aussi les adresses e-mail des comptes.",
  "auth.reset.request": "Demander un code",
  "auth.reset.again": "Demander un nouveau code",
  "auth.reset.opened": "Le fichier contenant le code est ouvert. Il se trouve ici :",
  "auth.reset.code": "Code",
  "auth.reset.newPassword": "Nouveau mot de passe",
  "auth.reset.save": "Enregistrer le mot de passe",
  "auth.reset.done": "Le mot de passe a été modifié. Connectez-vous avec le nouveau mot de passe.",
  "auth.reset.toLogin": "Vers la connexion",
  "auth.reset.back": "Retour à la connexion",
  "auth.setupTitle": "Configurer Rhinvoice",
  "auth.setupDescription":
    "Créez le premier compte utilisateur. Il aura accès à toutes les données de cette installation.",
  "auth.displayName": "Votre nom",
  "auth.newPassword": "Mot de passe",
  "auth.passwordHint":
    "Au moins 12 caractères. Une phrase de plusieurs mots est sûre et facile à retenir.",
  "auth.setupSubmit": "Créer le compte",
  "auth.loggedOut": "Vous avez été déconnecté.",
  "auth.sessionExpired": "Votre session a expiré. Veuillez vous reconnecter.",
  "auth.demoReset":
    "La démo a été réinitialisée, comme toutes les {minutes} minutes. Tout le monde est alors déconnecté : veuillez vous reconnecter.",

  "validation.required": "Veuillez remplir ce champ.",
  "validation.email": "Veuillez saisir une adresse e-mail valide, p. ex. nom@entreprise.ch.",
  "validation.passwordLength": "Le mot de passe doit comporter au moins 12 caractères.",
  "validation.totp": "Le code se compose de 6 chiffres.",
  "validation.summary": {
    one: "{count} information doit être corrigée",
    other: "{count} informations doivent être corrigées",
  },

  "error.demo_locked": "Ce n’est pas possible dans la démo publique.",
  "error.update_running": "La mise à jour est déjà en cours d’installation.",
  "error.update_check_failed":
    "Les informations sur les nouvelles versions n’ont pas pu être récupérées. L’ordinateur est-il connecté à Internet ?",
  "error.update_check_off": "La recherche de mises à jour est désactivée dans cette installation.",
  "error.no_update": "Il n’y a aucune mise à jour à installer ici.",
  "error.generic": "Une erreur s’est produite. Veuillez réessayer.",
  "error.network": "Pas de connexion au serveur. Veuillez vérifier votre connexion.",
  "error.authentication_failed": "L’adresse e-mail ou le mot de passe est incorrect.",
  "error.session_expired": "Votre session a expiré. Veuillez vous reconnecter.",
  "error.not_logged_in": "Vous n’êtes pas connecté.",
  "error.reset_code_invalid":
    "L’adresse e-mail ou le code est incorrect, ou le code a expiré. Après cinq tentatives infructueuses, un nouveau code est nécessaire.",
  "error.reset_by_administrator":
    "Ici, c’est un administrateur qui réinitialise le mot de passe sur le serveur.",
  "error.rate_limited": "Trop de tentatives depuis cette adresse. Veuillez patienter une minute.",
  "error.account_locked":
    "Trop de tentatives infructueuses. Le compte est bloqué pendant 15 minutes.",
  "error.totp_required": "Veuillez saisir le code de confirmation.",
  "error.retry": "Réessayer",
  "error.mail_not_configured":
    "L’envoi d’e-mails n’est pas encore configuré (Paramètres → E-mail).",
  "error.mail_draft": "Les brouillons ne sont pas envoyés. Veuillez d’abord émettre le document.",
  "error.mail_auth_failed":
    "Le serveur de messagerie a refusé le nom d’utilisateur ou le mot de passe.",
  "error.mail_recipient_refused":
    "Le serveur de messagerie a refusé un destinataire. Veuillez vérifier l’adresse.",
  "error.mail_sender_refused":
    "Le serveur de messagerie n’accepte pas l’adresse d’expéditeur. En général, elle doit appartenir au compte chez le fournisseur.",
  "error.mail_tls_failed":
    "La connexion chiffrée au serveur de messagerie a échoué. Veuillez vérifier le port et le chiffrement.",
  "error.mail_timeout": "Le serveur de messagerie n’a pas répondu à temps.",
  "error.mail_connection_failed":
    "Pas de connexion au serveur de messagerie. Veuillez vérifier le serveur et le port.",
  "error.mail_failed": "Le serveur de messagerie n’a pas accepté l’e-mail.",
  "error.recipient_missing": "Veuillez indiquer au moins une adresse e-mail.",
  "error.invalid_recipient": "Une adresse de destinataire n’est pas valide.",
  "error.mail_partly_refused":
    "Le serveur de messagerie a refusé une partie des destinataires ; les autres ont reçu l’e-mail (noté dans l’historique).",
  "error.mail_too_large":
    "L’e-mail est plus volumineux que ce qu’accepte le serveur de messagerie.",
  "error.mail_utf8_unsupported":
    "Le serveur de messagerie n’accepte pas les adresses contenant des trémas ou des accents.",
  "error.mail_login_needs_tls":
    "Une connexion nécessite une liaison chiffrée (STARTTLS ou SSL/TLS).",
  "error.mail_password_required":
    "Pour un autre serveur, port ou utilisateur, veuillez saisir à nouveau le mot de passe.",
  "error.reminder_not_open": "La facture n’est plus ouverte ; ses rappels ne sont plus envoyés.",
  "error.refusedRecipients": "Refusé : {refused}.",
  "error.serverSaid": "Réponse du serveur : « {reply} »",
  "error.not_found": "Introuvable. L’élément a peut-être été supprimé entre-temps.",
  "error.invalid_state":
    "Cette action n’est pas possible dans le statut actuel. Veuillez recharger la page.",
  "error.bank_account_missing":
    "Aucun compte bancaire n’est enregistré pour cette monnaie. Veuillez le créer sous Paramètres → Comptes bancaires.",
  "error.bank_account_unknown": "Ce compte bancaire n’existe plus. Veuillez en choisir un autre.",
  "error.bank_account_currency": "Le compte bancaire est dans une autre monnaie que le document.",
  "error.holder_address_missing": "Le compte d’un tiers nécessite l’adresse du titulaire.",
  "error.no_positions": "Le document nécessite au moins une position avec un montant.",
  "error.non_positive_total":
    "Le total d’une facture doit être supérieur à zéro. Les notes de crédit résultent d’une annulation.",
  "error.due_before_issue": "La date d’échéance est antérieure à la date du document.",
  "error.reverse_charge_needs_vat_number":
    "Pour l’autoliquidation (reverse charge), le client doit avoir un IDE / n° TVA. Veuillez le compléter dans la fiche client.",
  "error.company_vat_number_missing":
    "Votre n° TVA ou IDE doit figurer sur la facture. Veuillez le compléter sous Paramètres → Entreprise ou y choisir « Non assujetti à la TVA / petite entreprise ».",
  "error.company_tax_id_missing":
    "Votre numéro fiscal ou n° TVA intracommunautaire doit figurer sur la facture. Veuillez le compléter sous Paramètres → Entreprise.",
  "error.company_vat_id_missing":
    "Pour une livraison intracommunautaire, votre n° TVA intracommunautaire ou IDE doit figurer sur la facture. Veuillez le compléter sous Paramètres → Entreprise.",
  "error.customer_vat_id_missing":
    "Pour une livraison intracommunautaire, le n° TVA intracommunautaire ou l’IDE du client doit figurer sur la facture. Veuillez le compléter dans la fiche client.",
  "error.goods_only_treatment":
    "La livraison intracommunautaire et la livraison à l’exportation ne s’appliquent qu’aux marchandises. Choisissez pour le document « Autoliquidation (reverse charge) » ou « Exportation ».",
  "error.customer_vat_number_required":
    "Pour les factures de plus de 10'000 EUR à des entreprises en Autriche, le n° TVA autrichien (UID) du client doit figurer. Veuillez le compléter dans la fiche client.",
  "error.issue_date_in_future": "La date ne peut pas être dans le futur.",
  "error.issue_date_before_last":
    "La date est antérieure au dernier document émis. Les numéros et les dates doivent être croissants.",
  "error.payment_exceeds_open_amount": "Le montant est supérieur au montant ouvert.",
  "error.payment_date_in_future": "La date de paiement ne peut pas être dans le futur.",
  "error.write_off_date_in_future": "La date de passage en perte ne peut pas être dans le futur.",
  "error.payment_dated_before_write_off":
    "Le paiement est daté d’avant le passage en perte. Annulez plutôt le passage en perte.",
  "error.written_off": "La facture a été passée en perte. Annulez d’abord le passage en perte.",
  "error.clock_went_back": "L’horloge du serveur retarde. Veuillez réessayer dans un instant.",
  "error.write_off_before_invoice":
    "La date de passage en perte ne peut pas être antérieure à la date de la facture.",
  "error.payment_before_write_off":
    "Ce paiement a été reçu avant le passage en perte. Annulez d’abord le passage en perte.",
  "error.unknown_placeholder":
    "Espace réservé inconnu. Les possibilités sont {nummer}, {datum}, {faellig}, {kunde}, {betrag}, {waehrung}, {firma}, {kontoinhaber} et {kontoinhaber_adresse}.",
  "error.already_converted": "Une facture a déjà été créée à partir de cette offre.",
  "error.pageDescription":
    "Cette page n’a pas pu être affichée. Vos données enregistrées ne sont pas concernées.",

  "dashboard.title": "Vue d’ensemble",
  "dashboard.welcome": "Bienvenue, {name}",
  "dashboard.emptyTitle": "Pas encore de factures",
  "dashboard.emptyDescription":
    "Dès que vous émettez des factures, vous voyez ici les montants ouverts, en retard et payés.",

  "settings.title": "Paramètres",
  "settings.account": "Compte",
  "settings.security": "Sécurité",
  "settings.interaction": "Utilisation",
  "settings.singleKeyShortcuts": "Raccourcis à une touche",
  "settings.singleKeyShortcutsHint":
    "Raccourcis comme N (nouvelle facture) ou G puis C (clients). Désactivez-les si vous utilisez la saisie vocale ou des technologies d’assistance. La palette de commandes ({mod} K) reste disponible.",
  "settings.changePassword": "Modifier le mot de passe",
  "settings.currentPassword": "Mot de passe actuel",
  "settings.newPassword": "Nouveau mot de passe",
  "settings.passwordChanged": "Mot de passe modifié. Les autres sessions ont été déconnectées.",
  "settings.totpTitle": "Authentification à deux facteurs",
  "settings.totpEnabled": "Activée",
  "settings.totpDisabled": "Non activée",
  "settings.totpDescription":
    "Protège en plus votre compte avec un code issu d’une application d’authentification.",
  "settings.totpEnable": "Configurer",
  "settings.totpDisable": "Désactiver",
  "settings.totpScan":
    "Ajoutez cette clé dans votre application d’authentification, puis saisissez le code affiché.",
  "settings.totpSecret": "Clé",
  "settings.totpConfirm": "Confirmer",
  "settings.totpActivated": "L’authentification à deux facteurs est activée.",
  "settings.totpDeactivated": "L’authentification à deux facteurs est désactivée.",
  "settings.totpDisableConfirm": "Veuillez confirmer avec votre mot de passe et un code actuel.",

  "common.save": "Enregistrer",
  "common.cancel": "Annuler",
  "common.close": "Fermer",
  "common.optional": "(facultatif)",
  "common.copy": "Copier",
  "common.copied": "Copié",

  "common.new": "Nouveau",
  "common.edit": "Modifier",
  "common.archive": "Archiver",
  "common.restore": "Restaurer",
  "common.archived": "Archivé",
  "common.delete": "Supprimer",
  "common.saved": "Enregistré",
  "common.saving": "Enregistrement …",
  "common.back": "Retour",
  "common.previous": "Page précédente",
  "common.next": "Page suivante",
  "common.pageInfo": "{from}–{to} sur {total}",
  "common.showArchived": "Afficher les éléments archivés",
  "common.undo": "Annuler",
  "common.opensInNewTab": " (s’ouvre dans un nouvel onglet)",
  "common.loadError": "Les données n’ont pas pu être chargées.",
  "common.none": "—",
  "common.default": "Par défaut",
  "common.notSet": "Non défini",
  "common.useDefault": "Utiliser la valeur par défaut",
  "common.saveShortcut": "Enregistrer ({mod} ↵)",
  "common.unsavedChanges": "Modifications non enregistrées",
  "common.results": { zero: "Aucun résultat", one: "1 résultat", other: "{count} résultats" },
  "address.legend": "Adresse",
  "address.street": "Rue",
  "address.houseNumber": "N°",
  "address.supplement": "Complément d’adresse",
  "address.supplementHint": "p. ex. c/o, case postale ou service",
  "address.postalCode": "NPA",
  "address.city": "Localité",
  "address.country": "Pays",
  "validation.maxLength": "{max} caractères au maximum.",
  "validation.number": "Veuillez saisir un nombre, p. ex. {example}.",
  "validation.range": "Veuillez saisir un nombre de {min} à {max}.",
  "validation.decimals": "{max} décimales au maximum.",
  "validation.percent": "Veuillez saisir une valeur entre 0 et 100.",
  "validation.days": "Veuillez saisir un nombre entier de 0 à 365.",
  "validation.positiveInteger": "Veuillez saisir un nombre entier positif.",
  "validation.iban.format":
    "Un IBAN commence par le code pays et deux chiffres de contrôle, p. ex. CH93 0076 …",
  "validation.iban.country": "Les IBAN de ce pays ne sont pas pris en charge.",
  "validation.iban.length":
    "Un IBAN de {country} comporte {length} caractères – veuillez vérifier.",
  "validation.iban.checksum":
    "Les chiffres de contrôle ne concordent pas – veuillez vérifier s’il y a une faute de frappe.",
  "validation.iban.isQr":
    "Il s’agit d’un QR-IBAN. Veuillez le saisir dans le champ QR-IBAN et indiquer ici l’IBAN normal.",
  "validation.iban.notQr":
    "Un QR-IBAN contient aux positions 5 à 9 un nombre entre 30000 et 31999.",
  "validation.bic": "Un BIC comporte 8 ou 11 caractères, p. ex. UBSWCHZH80A.",
  "validation.vat.swiss-format": "Un IDE a la forme CHE-123.456.789.",
  "validation.vat.swiss-check-digit":
    "Le chiffre de contrôle de l’IDE ne concorde pas – veuillez vérifier.",
  "validation.vat.german-format": "Un n° TVA allemand a la forme DE123456789.",
  "validation.vat.austrian-format": "Un n° TVA autrichien (UID) a la forme ATU12345678.",
  "validation.numberTaken": "Ce numéro est déjà attribué.",
  "validation.server": "Veuillez vérifier cette information ({message}).",
  "customers.title": "Clients",
  "customers.new": "Nouveau client",
  "customers.edit": "Modifier le client",
  "customers.emptyTitle": "Pas encore de clients",
  "customers.emptyDescription":
    "Créez votre premier client. L’adresse et les conditions seront reprises plus tard dans les factures.",
  "customers.noResults": "Aucun client pour cette recherche.",
  "customers.resetSearch": "Réinitialiser la recherche",
  "customers.searchLabel": "Rechercher des clients",
  "customers.searchPlaceholder": "Nom, numéro, localité, e-mail …",
  "customers.number": "N°",
  "customers.numberAndSince": "Numéro de client {number} · Client depuis {since}",
  "customers.numberLabel": "Numéro de client",
  "customers.numberHint": "Laisser vide pour le prochain numéro libre.",
  "customers.name": "Nom",
  "customers.city": "Localité",
  "customers.email": "E-mail",
  "customers.group": "Groupe",
  "customers.kind": "Type",
  "customers.kindCompany": "Entreprise",
  "customers.kindPerson": "Particulier",
  "customers.sectionBasics": "Données de base",
  "customers.sectionContact": "Contact",
  "customers.sectionBilling": "Facturation",
  "customers.sectionDelivery": "Adresse de livraison",
  "customers.companyName": "Raison sociale",
  "customers.salutation": "Civilité",
  "customers.salutationHint": "p. ex. Monsieur, Madame ou vide",
  "customers.firstName": "Prénom",
  "customers.lastName": "Nom",
  "customers.contactPerson": "Personne de contact",
  "customers.contactPersonHint": "Apparaît comme « à l’att. de » dans l’adresse.",
  "customers.phone": "Téléphone",
  "customers.vatNumber": "IDE / n° TVA",
  "customers.vatNumberHint":
    "Obligatoire pour l’autoliquidation (reverse charge) avec des entreprises clientes dans l’UE.",
  "customers.buyerReference": "Leitweg-ID ou référence d’acheteur fixe",
  "customers.buyerReferenceHint":
    "Pour la XRechnung. Autorités : leur Leitweg-ID. Entreprises : à remplir seulement si elles souhaitent toujours la même référence. Si la référence change à chaque commande (p. ex. le numéro de commande), laissez ce champ vide et saisissez-la dans la facture sous « Autres informations → Référence ».",
  "customers.defaultCurrency": "Monnaie par défaut",
  "customers.defaultLanguage": "Langue des documents",
  "customers.taxExempt": "Exonéré de la TVA",
  "customers.taxExemptHint":
    "Uniquement pour les clients exonérés de la TVA, p. ex. ambassades ou organisations internationales. Pas pour les entreprises à l’étranger : une entreprise de l’UE avec n° TVA (p. ex. ATU12345678) reçoit automatiquement l’autoliquidation (reverse charge), une entreprise hors UE automatiquement une facture sans TVA (exportation).",
  "customers.taxExemptReverseCharge":
    "Cette entreprise a un n° TVA dans l’UE : sans « Exonéré de la TVA », elle reçoit automatiquement l’autoliquidation (reverse charge, TVA due par le preneur). Avec « Exonéré de la TVA », la mention « Prestation exonérée de la TVA » figure à la place sur la facture.",
  "customers.defaultDiscount": "Rabais par défaut en %",
  "customers.paymentTerms": "Délai de paiement en jours",
  "customers.paymentTermsHint": "Laisser vide pour le délai de paiement de l’entreprise.",
  "customers.hasDelivery": "Adresse de livraison différente",
  "customers.note": "Note interne",
  "customers.noteHint": "N’apparaît pas sur les documents.",
  "customers.created": "Client « {name} » créé.",
  "customers.sheetHint":
    "La facture reste ouverte derrière. Après l’enregistrement, vous y revenez.",
  "customers.createAndChoose": "Créer et choisir",
  "customers.archivedToast": "« {name} » archivé.",
  "customers.restoredToast": "« {name} » restauré.",
  "customers.archivedNotice":
    "Ce client est archivé et n’apparaît pas dans les listes de sélection.",
  "customers.documentsTitle": "Documents",
  "customers.documentsEmpty": "Il n’y a pas encore de documents pour ce client.",
  "articles.title": "Articles",
  "articles.new": "Nouvel article",
  "articles.edit": "Modifier l’article",
  "articles.emptyTitle": "Pas encore d’articles",
  "articles.emptyDescription":
    "Saisissez des produits et des prestations avec prix et unité pour les insérer rapidement dans les factures.",
  "articles.noResults": "Aucun article pour cette recherche.",
  "articles.searchLabel": "Rechercher des articles",
  "articles.searchPlaceholder": "Désignation, numéro, description …",
  "articles.number": "N°",
  "articles.numberLabel": "Numéro d’article",
  "articles.name": "Désignation",
  "articles.description": "Description",
  "articles.descriptionHint": "Apparaît sous la désignation sur le document.",
  "articles.group": "Groupe",
  "articles.unit": "Unité",
  "articles.taxCategory": "Taux de TVA",
  "articles.supply": "Marchandise ou prestation de services",
  "articles.supplyHint":
    "Sur le plan fiscal, les marchandises passent la frontière autrement que les prestations de services (exportation, livraison intracommunautaire).",
  "articles.price": "Prix",
  "articles.prices": "Prix (hors TVA)",
  "articles.pricesHint": "Laisser vide si l’article n’est pas proposé dans cette monnaie.",
  "articles.priceIn": "Prix en {currency}",
  "articles.created": "Article « {name} » créé.",
  "articles.archivedToast": "« {name} » archivé.",
  "articles.restoredToast": "« {name} » restauré.",
  "articles.archivedNotice":
    "Cet article est archivé et n’apparaît pas dans les listes de sélection.",
  "tax.standard": "Taux normal",
  "tax.reduced": "Taux réduit",
  "tax.special": "Taux spécial",
  "tax.zero": "0 % (imposable)",
  "tax.exempt": "Exclu du champ de l’impôt",
  "supply.service": "Prestation de services",
  "supply.goods": "Marchandise",
  "settings.tabMail": "E-mail",
  "settings.tabCompany": "Entreprise",
  "settings.tabBankAccounts": "Comptes bancaires",
  "settings.tabNumbering": "Numérotations",
  "settings.tabTexts": "Textes",
  "texts.intro":
    "Les modèles de texte remplissent automatiquement les nouveaux documents. Les modèles marqués « Par défaut » sont insérés, vous pouvez choisir tous les autres dans l’éditeur.",
  "texts.documentType": "Type de document",
  "texts.kind.intro": "Introduction",
  "texts.kind.payment": "Conditions de paiement",
  "texts.kind.closing": "Texte final",
  "texts.kindHint.intro": "Figure avant les positions, p. ex. un remerciement pour la commande.",
  "texts.kindHint.payment":
    "Figure après les totaux. Plusieurs modèles par défaut sont placés les uns sous les autres.",
  "texts.kindHint.closing": "Figure à la fin du document, p. ex. une salutation.",
  "texts.add": "Ajouter",
  "texts.addTitle": "Nouveau modèle de texte",
  "texts.editTitle": "Modifier le modèle de texte",
  "texts.empty": "Aucun modèle de texte pour l’instant.",
  "texts.default": "Par défaut",
  "texts.moveUp": "Monter « {name} »",
  "texts.moveDown": "Descendre « {name} »",
  "texts.edit": "Modifier « {name} »",
  "texts.delete": "Supprimer « {name} »",
  "texts.deleteTitle": "Supprimer le modèle de texte ?",
  "texts.deleteDescription":
    "« {name} » sera supprimé. Les documents qui contiennent déjà ce texte restent inchangés.",
  "texts.deleted": "Modèle de texte supprimé",
  "texts.name": "Nom",
  "texts.nameHint":
    "Court, c’est ainsi qu’il apparaît dans l’éditeur, p. ex. « Payable immédiatement ».",
  "texts.text": "Texte",
  "texts.placeholders": "Insérer un espace réservé",
  "texts.insertPlaceholder": "Insérer l’espace réservé pour {placeholder}",
  "texts.placeholder.nummer": "Numéro",
  "texts.placeholder.datum": "Date",
  "texts.placeholder.faellig": "Échéance ou validité",
  "texts.placeholder.kunde": "Client",
  "texts.placeholder.betrag": "Montant",
  "texts.placeholder.waehrung": "Monnaie",
  "texts.placeholder.firma": "Votre entreprise",
  "texts.placeholder.kontoinhaber": "Titulaire du compte",
  "texts.placeholder.kontoinhaber_adresse": "Adresse du titulaire du compte",
  "texts.useAsDefault": "Insérer dans les nouveaux documents (par défaut)",
  "texts.notices": "Mentions légales",
  "texts.noticesHint":
    "Apparaissent automatiquement selon le cas fiscal. Lors de l’émission, le libellé est enregistré avec le document. Ceci n’est pas un conseil fiscal : faites-le vérifier.",
  "texts.noticeBuiltIn": "Libellé proposé pour votre pays.",
  "texts.noticeCustomized": "Libellé personnalisé.",
  "texts.restoreNotice": "Restaurer la proposition",
  "settings.tabSecurity": "Sécurité",
  "settings.tabInteraction": "Utilisation",
  "company.title": "Entreprise",
  "company.description": "Ces informations figurent comme expéditeur sur tous les documents.",
  "company.setupHint": "Veuillez d’abord saisir les données de votre entreprise.",
  "company.name": "Nom de l’entreprise",
  "company.sectionContact": "Contact",
  "company.sectionTax": "Impôts et registre",
  "company.sectionDefaults": "Préréglages",
  "company.sectionAppearance": "Apparence",
  "company.email": "E-mail",
  "company.phone": "Téléphone",
  "company.website": "Site web",
  "company.vatNumber": "IDE / n° TVA / n° TVA intracommunautaire",
  "company.vatNumberHint": "p. ex. CHE-123.456.789 TVA ou DE123456789",
  "company.taxNumber": "Numéro fiscal",
  "company.taxNumberHint":
    "Numéro fiscal allemand, si vous n’avez pas de n° TVA intracommunautaire.",
  "company.taxDialogTitle": "Données fiscales de l’entreprise",
  "company.taxDialogHint":
    "Ces données figurent sur chaque facture et s’appliquent dès maintenant à toute l’entreprise.",
  "company.taxDialogOtherErrors":
    "D’autres données de l’entreprise sont incomplètes. Veuillez les vérifier sous Paramètres → Entreprise.",
  "company.commercialRegister": "Registre du commerce",
  "company.commercialRegisterHint":
    "p. ex. Registre du commerce du canton de Vaud, CHE-123.456.789",
  "company.smallBusiness": "Non assujetti à la TVA / petite entreprise (franchise de TVA)",
  "company.smallBusinessHint":
    "Les factures ne contiennent alors pas de TVA, mais la mention exigée par la loi.",
  "company.cashRounding": "Arrondi aux 5 centimes",
  "company.cashRoundingHint": "Arrondir les montants en CHF à 0.05.",
  "company.defaultCurrency": "Monnaie par défaut",
  "company.defaultLanguage": "Langue des documents",
  "company.paymentTerms": "Délai de paiement en jours",
  "company.accentHue": "Couleur d’accent",
  "company.accentHueHint":
    "Teinte de l’interface (0–359). Toutes les teintes sont vérifiées pour la lisibilité.",
  "company.saved": "Données de l’entreprise enregistrées.",
  "company.logo": "Logo",
  "company.logoHint": "PNG, JPEG ou WebP, 1 Mo au maximum. Apparaît en haut des documents.",
  "company.logoUpload": "Téléverser le logo",
  "company.logoReplace": "Remplacer le logo",
  "company.logoRemove": "Retirer le logo",
  "company.logoAlt": "Logo actuel de l’entreprise",
  "company.logoUploaded": "Logo enregistré.",
  "company.logoRemoved": "Logo retiré.",
  "company.logoTooLarge": "Le logo ne doit pas dépasser 1 Mo.",
  "language.de": "Allemand",
  "language.en": "Anglais",
  "language.fr": "Français",
  "language.it": "Italien",
  "bank.title": "Comptes bancaires",
  "bank.description":
    "Comptes pour les informations de paiement et les codes QR. Par monnaie, un compte est le compte par défaut; un document peut en choisir un autre.",
  "bank.add": "Ajouter un compte",
  "bank.edit": "Modifier le compte",
  "bank.emptyTitle": "Aucun compte bancaire pour l’instant",
  "bank.emptyDescription": "Saisissez au moins un compte par monnaie de facturation.",
  "bank.bankName": "Banque",
  "bank.holder": "Titulaire du compte",
  "bank.iban": "IBAN",
  "bank.qrIban": "QR-IBAN",
  "bank.qrIbanHint": "Uniquement pour les factures QR avec référence QR (Suisse et Liechtenstein).",
  "bank.bic": "BIC / SWIFT",
  "bank.bicEpcHint":
    "Nécessaire pour le code QR EPC sur les factures en euros aux clients de l’UE : sans BIC, il n’y en a pas.",
  "bank.currency": "Monnaie",
  "bank.isDefault": "Compte par défaut pour cette monnaie",
  "bank.thirdParty": "Le compte appartient à un tiers (p. ex. un prestataire financier)",
  "bank.thirdPartyHint":
    "Les documents payés sur ce compte nomment le titulaire comme bénéficiaire. Son adresse figure sur la section paiement QR.",
  "bank.thirdPartyBadge": "Compte de tiers",
  "bank.holderAddress": "Adresse du titulaire du compte",
  "bank.saved": "Compte bancaire enregistré.",
  "bank.deleteTitle": "Supprimer le compte bancaire ?",
  "bank.deleteDescription":
    "Les documents déjà émis conservent leurs coordonnées bancaires. Les brouillons et les nouveaux documents utilisent le compte par défaut de leur monnaie.",
  "bank.deleted": "Compte bancaire supprimé.",
  "numbering.title": "Numérotations",
  "numbering.description":
    "Les numéros de facture ne sont attribués qu’à l’émission – sans lacune et uniques.",
  "numbering.kind.invoice": "Factures",
  "numbering.kind.quote": "Offres",
  "numbering.kind.cancellation": "Factures d’annulation",
  "numbering.kind.customer": "Clients",
  "numbering.kind.article": "Articles",
  "numbering.pattern": "Modèle",
  "numbering.patternHint":
    "{YYYY} année, {YY} année à deux chiffres, {MM} mois, {NNNN} compteur (nombre de N = nombre minimal de chiffres).",
  "numbering.nextValue": "Prochain compteur",
  "numbering.nextValueHint": "Pour enchaîner sans rupture avec un programme précédent.",
  "numbering.startValue": "Valeur initiale après le changement d’année",
  "numbering.yearlyReset": "Remettre à zéro chaque année",
  "numbering.preview": "Prochain numéro",
  "numbering.saved": "Numérotation enregistrée.",
  "numbering.problem.length": "Le modèle doit comporter de 1 à 40 caractères.",
  "numbering.problem.placeholder":
    "Espace réservé inconnu. Sont autorisés {YYYY}, {YY}, {MM} et {N…}.",
  "numbering.problem.counter": "Le modèle doit contenir exactement un compteur comme {NNNN}.",
  "numbering.problem.characters":
    "Sont autorisés les lettres, les chiffres et - _ / . ainsi que les espaces réservés.",
  "numbering.problem.year":
    "Avec une remise à zéro annuelle, le modèle doit contenir {YYYY} ou {YY}, sinon des numéros se répètent.",
  "shortcuts.newQuote": "Nouvelle offre",
  "shortcuts.newCustomer": "Nouveau client",
  "shortcuts.newArticle": "Nouvel article",
  "shortcuts.or": "ou",
  "shortcuts.undo": "Annuler la dernière action (tant que le message est visible)",
  "shortcuts.payment": "Saisir un paiement (dans le document)",
  "shortcuts.moveLineUp": "Monter la position (dans l’éditeur)",
  "shortcuts.moveLineDown": "Descendre la position (dans l’éditeur)",
  "shortcuts.goQuotes": "Aller aux offres",

  "dashboard.asTable": "Sous forme de tableau",
  "dashboard.currency": "Monnaie",
  "dashboard.revenueYear": "Chiffre d’affaires {year}",
  "dashboard.vsLastYear": "par rapport à la même période de l’année précédente",
  "dashboard.noComparison": "pas d’année précédente",
  "dashboard.showInvoices": "Voir les factures",
  "dashboard.showOpen": "Factures ouvertes",
  "dashboard.showDunning": "Vers les relances",
  "dashboard.showQuotes": "Voir les offres ouvertes",
  "dashboard.daysToPay": {
    zero: "Les clients paient le jour même",
    one: "Ø payée après 1 jour",
    other: "Ø payée après {count} jours",
  },
  "dashboard.oldestOverdue": {
    zero: "échue aujourd’hui",
    one: "la plus ancienne depuis 1 jour",
    other: "la plus ancienne depuis {count} jours",
  },
  "dashboard.openQuotes": "Offres ouvertes",
  "dashboard.quoteCount": { zero: "Aucune offre", one: "1 offre", other: "{count} offres" },
  "dashboard.acceptance": "Taux d’acceptation {rate} cette année",
  "dashboard.noDecisions": "Aucune offre tranchée pour l’instant",
  "dashboard.aging": "Ouvert par échéance",
  "dashboard.nothingOpen": "Aucune facture ouverte.",
  "dashboard.age.not_due": "Pas encore échue",
  "dashboard.age.late_30": "1–30 jours de retard",
  "dashboard.age.late_60": "31–60 jours de retard",
  "dashboard.age.late_more": "Plus de 60 jours de retard",
  "dashboard.overdueList": "En retard depuis le plus longtemps",
  "dashboard.noOverdue": "Aucune facture en retard.",
  "dashboard.daysLate": {
    zero: "échue aujourd’hui",
    one: "1 jour de retard",
    other: "{count} jours de retard",
  },
  "dashboard.remind": "Vers les relances",
  "dashboard.topCustomers": "Meilleurs clients {year}",
  "dashboard.topCustomersHint": "Chiffre d’affaires net, déduction faite des annulations.",
  "dashboard.noCustomerRevenue": "Pas encore de chiffre d’affaires cette année.",
  "dashboard.expected": "Paiements attendus",
  "dashboard.expectedHint":
    "Factures ouvertes par semaine d’échéance ; cette semaine avec tout ce qui est en retard.",
  "dashboard.week": "Semaine",
  "dashboard.weekLabel": "Sem. {week}",
  "dashboard.amount": "Montant",
  "dashboard.figuresIn": "Chiffres clés en {currency}",
  "dashboard.open": "Ouvert",
  "dashboard.openCount": {
    zero: "Aucune facture ouverte",
    one: "1 facture ouverte",
    other: "{count} factures ouvertes",
  },
  "dashboard.overdue": "En retard",
  "dashboard.overdueCount": {
    zero: "Rien en retard",
    one: "1 facture en retard",
    other: "{count} factures en retard",
  },
  "dashboard.paid": "Payé",
  "dashboard.paidHint": "Somme de tous les paiements reçus",
  "dashboard.revenue": "Chiffre d’affaires par mois",
  "dashboard.revenueHint": "Chiffre d’affaires net en {currency}, déduction faite des annulations.",
  "dashboard.revenueIn": "Chiffre d’affaires en {currency}",
  "dashboard.noRevenue": "Aucune facture n’a été émise au cours des 12 derniers mois.",
  "dashboard.month": "Mois",
  "dashboard.netRevenue": "Chiffre d’affaires net",
  "dashboard.activity": "Activités récentes",
  "dashboard.noActivity": "Aucune activité pour l’instant.",

  "documentType.invoice": "Facture",
  "documentType.quote": "Offre",
  "documentType.cancellation": "Facture d’annulation",

  "status.draft": "Brouillon",
  "status.sent": "Envoyée",
  "status.accepted": "Acceptée",
  "status.declined": "Refusée",
  "status.expired": "Expirée",
  "status.issued": "Émise",
  "status.partially_paid": "Partiellement payée",
  "status.overdue": "En retard",
  "status.overdueDays": {
    zero: "En retard",
    one: "En retard · 1 jour",
    other: "En retard · {count} jours",
  },
  "status.paid": "Payée",
  "status.cancelled": "Annulée",
  "status.written_off": "Passée en perte",

  "event.emailed": "Envoyé par e-mail",
  "event.reminder_emailed": "Rappel envoyé par e-mail",
  "event.created": "Créé",
  "event.created_from": "Créé à partir de",
  "event.issued": "Émis",
  "event.sent": "Envoyé",
  "event.accepted": "Accepté",
  "event.declined": "Refusé",
  "event.converted": "Converti en facture",
  "event.payment_added": "Paiement saisi",
  "event.payment_removed": "Paiement retiré",
  "event.cancelled": "Annulé",
  "event.tax_overridden": "Cas fiscal choisi manuellement",
  "event.reminded": "Rappel rédigé",
  "event.imported": "Repris d’un programme précédent",
  "event.file_added": "Fichier ajouté",
  "event.written_off": "Passé en perte",
  "event.write_off_removed": "Passage en perte annulé",

  "treatment.domestic": "National (imposable)",
  "treatment.reverse_charge": "Autoliquidation (reverse charge)",
  "treatment.export": "Exportation (exonérée)",
  "treatment.small_business": "Petite entreprise (sans TVA)",
  "treatment.exempt": "Exonéré de la TVA",
  "treatment.intra_community": "Livraison intracommunautaire (exonérée)",
  "treatment.goods_export": "Livraison à l’exportation (exonérée)",

  "documents.newInvoice": "Nouvelle facture",
  "documents.newQuote": "Nouvelle offre",
  "documents.draftTitle": "{kind} (brouillon)",
  "documents.numberedTitle": "{kind} {number}",
  "documents.searchLabel": "Rechercher dans les documents",
  "documents.searchPlaceholder": "Numéro, client, objet, position …",
  "documents.views": "Vue",
  "documents.view.all": "Tous",
  "documents.view.draft": "Brouillons",
  "documents.view.open": "Ouvertes",
  "documents.view.overdue": "En retard",
  "documents.view.paid": "Payées",
  "documents.view.cancelled": "Annulées",
  "documents.view.written_off": "Passées en perte",
  "documents.view.sent": "Envoyées",
  "documents.view.accepted": "Acceptées",
  "documents.view.declined": "Refusées",
  "documents.view.expired": "Expirées",
  "documents.noResults": "Aucun document pour ce filtre.",
  "documents.resetFilter": "Réinitialiser le filtre",
  "documents.emptyInvoices": "Aucune facture pour l’instant",
  "documents.emptyInvoicesDescription":
    "Créez votre première facture. Le numéro n’est attribué qu’à l’émission.",
  "documents.emptyQuotes": "Aucune offre pour l’instant",
  "documents.emptyQuotesDescription":
    "Créez une offre et, une fois acceptée, convertissez-la en facture d’un seul clic.",
  "documents.number": "Numéro",
  "documents.status": "Statut",
  "documents.amount": "Montant",
  "documents.openShort": "ouvert {amount}",
  "documents.applyLine": "Appliquer",
  "documents.customerNumber": "N° client {number}",
  "documents.customerSearch": "Rechercher un client",
  "documents.changeCustomer": "Modifier",
  "documents.discardLine": "Rejeter",
  "documents.saveAsArticle": "Enregistrer comme article",
  "documents.articleSaved": "Article {number} « {name} » enregistré.",
  "documents.dueInDays": "{days} jours",
  "documents.dueNow": "Immédiatement",
  "documents.editLine": "Modifier la position {number} : {description}",
  "documents.editPane": "Modifier",
  "documents.insertTemplate": "Insérer un modèle de texte …",
  "documents.linePriceIn": "Prix {currency}",
  "documents.manageTemplates": "Gérer les modèles de texte",
  "documents.netTotal": "Net {amount}",
  "documents.noLines":
    "Aucune position pour l’instant. Ajoutez-en une ; des articles sont proposés pendant la saisie.",
  "documents.noticeAuto": "Mention automatique :",
  "documents.paneSwitch": "Vue",
  "documents.paymentTemplates": "Conditions de paiement issues des modèles de texte",
  "documents.paymentText": "Conditions de paiement",
  "documents.payWithin": "Délai de paiement",
  "documents.placeholdersHint":
    "Les espaces réservés pour le numéro, l’échéance, le client et le montant sont remplacés à l’impression.",
  "documents.previewHint":
    "L’aperçu montre le document tel qu’il sera imprimé. Un clic sur une position l’ouvre dans la liste.",
  "documents.previewPane": "Aperçu",
  "documents.cannotMarkPaid":
    "{number} ne peut pas être marquée comme payée (entre-temps payée, annulée ou brouillon). Rien n’a été modifié.",
  "documents.popupBlocked":
    "Le navigateur a bloqué la nouvelle fenêtre. Veuillez autoriser les fenêtres pop-up pour Rhinvoice ou utiliser « Télécharger ».",
  "error.unavailable":
    "Le service PDF n’est pas joignable pour le moment. Veuillez réessayer plus tard ou vous adresser à la personne qui exploite Rhinvoice.",
  "error.cannot_mark_paid":
    "L’une des factures sélectionnées ne peut pas être marquée comme payée (entre-temps payée ou annulée). Rien n’a été modifié.",
  "documents.autosaveFailed":
    "L’enregistrement automatique a échoué. Veuillez enregistrer manuellement.",
  "documents.pdf": "PDF",
  "documents.download": "Télécharger",
  "documents.print": "Imprimer",
  "documents.share": "Partager",
  "documents.viewMode": "Affichage",
  "documents.viewDocument": "Document",
  "documents.viewDetails": "Détails",
  "documents.imported": "Reprise",
  "documents.importedHint":
    "Reprise d’un programme précédent. Le PDF constitue la facture ; elle ne comporte pas de positions.",
  "documents.originalPdf": "PDF original",
  "documents.noPdf": "Pas encore de PDF",
  "documents.noPdfHint":
    "Aucun PDF n’a été trouvé pour cette facture lors de la reprise. Ajoutez-le pour qu’elle soit complète. Il ne pourra ensuite plus être remplacé.",
  "documents.addPdf": "Ajouter un PDF",
  "documents.attachmentsTitle": "Pièces jointes",
  "documents.attachmentsHint": "Rappels, corrections et copies du programme précédent.",
  "documents.attachmentsEmpty": "Aucune pièce jointe.",
  "documents.addPdfTitle": "Ajouter un PDF ?",
  "documents.addAttachmentTitle": "Joindre un fichier ?",
  "documents.addFileConfirm":
    "« {name} » sera conservé durablement avec cette facture. Il ne pourra ensuite être ni remplacé ni retiré.",
  "documents.addAttachment": "Joindre un fichier",
  "documents.fileAdded": "Fichier ajouté.",
  "documents.pdfAdded": "PDF ajouté.",
  "documents.addFileHint":
    "Uniquement des PDF, 20 Mo au maximum. Les fichiers ajoutés ne peuvent plus être retirés.",
  "documents.paidOnUnknown":
    "Date de paiement inconnue dans le programme précédent ; reprise comme date d’échéance.",
  "documents.pdfLoading": "Chargement du PDF …",
  "documents.previewEmpty": "L’aperçu apparaît dès qu’un client et une position sont saisis.",
  "documents.previewTitle": "Aperçu : {title}",
  "documents.reverseChargeHint":
    "Autoliquidation (reverse charge) : pas de {tax}. La mention et l’IDE / le n° TVA du client figurent automatiquement sur le document.",
  "documents.sectionMore": "Autres informations",
  "documents.sectionMoreHint": "Objet, référence, période de prestation, monnaie, rabais",
  "documents.sectionTexts": "Textes",
  "documents.sectionTextsHint": "Introduction, conditions de paiement, texte final",
  "documents.filters": "Filtres",
  "documents.year": "Année",
  "documents.allYears": "Toutes les années",
  "documents.customerFilter": "Client",
  "documents.allCustomers": "Tous les clients",
  "documents.removeCustomerFilter": "Retirer le filtre client",
  "documents.selectDocument": "Sélectionner {number}",
  "documents.selectAllPayable": "Sélectionner toutes les factures ouvertes de cette page",
  "documents.markPaidShort": "Payée",
  "documents.markPaidRow": "Marquer {number} comme payée (aujourd’hui, montant total)",
  "documents.selection": "Sélection",
  "documents.selectedCount": { one: "{count} sélectionné", other: "{count} sélectionnés" },
  "documents.markSelectedPaid": "Marquer comme payées",
  "documents.clearSelection": "Annuler la sélection",
  "documents.markedPaidNumber": "{number} marquée comme payée",
  "documents.markedPaidMany": "{count} factures marquées comme payées",
  "documents.paymentsUndone": "Paiements retirés",
  "documents.sums": "Totaux",
  "documents.sumCountInvoices": {
    one: "{count} facture en {currency}",
    other: "{count} factures en {currency}",
  },
  "documents.sumCountQuotes": {
    one: "{count} offre en {currency}",
    other: "{count} offres en {currency}",
  },
  "documents.sumTotal": "Total {amount}",
  "documents.sumOpen": "dont ouvert {amount}",
  "documents.companyMissingTitle": "Données de l’entreprise manquantes",
  "documents.companyMissing":
    "Veuillez d’abord saisir les données de votre entreprise. Elles figurent comme expéditeur sur chaque document.",
  "documents.facts": "Données clés",
  "documents.sectionRecipient": "Destinataire et objet",
  "documents.customer": "Client",
  "documents.customerPlaceholder": "Saisir un nom, un numéro ou une localité …",
  "documents.customerRequired": "Veuillez choisir un client.",
  "documents.noCustomerFound": "Aucun client trouvé.",
  "documents.createCustomerNamed": "Créer « {name} » comme nouveau client",
  "documents.noCustomersTitle": "Pas encore de clients.",
  "documents.noCustomersHint":
    "Une facture a besoin d’un destinataire. Créez-le ici, la facture reste ouverte.",
  "documents.createCustomer": "Créer un client",
  "documents.importCustomers": "Importer des clients",
  "documents.blockersTitle": "Il manque encore avant l’émission :",
  "documents.blockersHint":
    "L’enregistrement fonctionne déjà ; seule l’émission attend ces données.",
  "blocker.bank_account_missing":
    "un compte bancaire en {currency} pour les informations de paiement",
  "blocker.reverse_charge_needs_vat_number": "le numéro de TVA du client pour l’autoliquidation",
  "blocker.company_vat_number_missing":
    "votre numéro de TVA, ou l’indication « non assujetti à la TVA »",
  "blocker.company_tax_id_missing": "votre numéro fiscal ou numéro de TVA",
  "blocker.customer_vat_number_required":
    "le numéro de TVA du client (factures de plus de 10 000 EUR)",
  "blocker.company_vat_id_missing": "votre numéro de TVA pour la livraison intracommunautaire",
  "blocker.customer_vat_id_missing":
    "le numéro de TVA du client pour la livraison intracommunautaire",
  "blocker.fix.bank_account": "Ajouter un compte {currency}",
  "blocker.fix.company": "Compléter les données fiscales",
  "blocker.fix.customer": "Compléter le client",
  "documents.subject": "Objet",
  "documents.subjectHint": "p. ex. « Refonte du site web, phase 2 »",
  "documents.reference": "Référence",
  "documents.referenceHint": "p. ex. numéro de commande du client",
  "documents.referenceHintXRechnung":
    "Numéro de commande ou similaire – nécessaire pour la XRechnung",
  "documents.referenceHintFixed": "La XRechnung utilise la Leitweg-ID du client : {value}",
  "documents.currency": "Monnaie",
  "documents.language": "Langue",
  "documents.sectionDates": "Dates",
  "documents.issueDate": "Date",
  "documents.issueDateHint":
    "Prérempli avec la date du jour. Laisser vide pour utiliser la date d’émission.",
  "documents.dueDate": "Échéance le",
  "documents.bankAccount": "Coordonnées bancaires",
  "documents.bankAccountHint": "Le compte sur lequel ce document est payé.",
  "documents.bankAccountDefault": "(par défaut)",
  "documents.bankAccountThirdParty": "(compte de tiers)",
  "documents.dueDateHint":
    "Laisser vide pour utiliser le délai de paiement du client ou de l’entreprise.",
  "documents.validUntil": "Valable jusqu’au",
  "documents.validUntilHint": "Laisser vide pour 30 jours à compter de la date.",
  "documents.serviceFrom": "Prestation du",
  "documents.serviceTo": "Prestation jusqu’au",
  "documents.serviceHint": "Détermine le taux de TVA ; sans indication, la date fait foi.",
  "documents.servicePeriod": "Période de prestation",
  "documents.dueBeforeIssue": "La date d’échéance est antérieure à la date.",
  "documents.serviceEndsBeforeStart": "La période de prestation se termine avant son début.",
  "documents.introText": "Texte d’introduction",
  "documents.closingText": "Texte final",
  "documents.closingTextHint":
    "p. ex. remerciements, indications de paiement ou validité de l’offre.",

  "documents.sectionLines": "Positions",
  "documents.priceMode": "Prix",
  "documents.priceModeNet": "Montant net (plus {tax})",
  "documents.priceModeGross": "Montant brut ({tax} incluse)",
  "documents.lineLabel": "Position {number}",
  "documents.lineError": "Position {number} : {message}",
  "documents.lineDescription": "Désignation",
  "documents.lineText": "Texte",
  "documents.lineTextPlaceholder": "Intertitre ou remarque",
  "documents.lineDetails": "Texte complémentaire de la position {number}",
  "documents.lineDetailsPlaceholder": "Texte complémentaire (apparaît sous la désignation)",
  "documents.addDetails": "Ajouter un texte complémentaire",
  "documents.lineQuantity": "Quantité",
  "documents.lineUnit": "Unité",
  "documents.linePrice": "Prix",
  "documents.lineDiscount": "Rabais %",
  "documents.lineDiscountApplied": "moins {percent} de rabais",
  "documents.lineTax": "Taux de TVA",
  "documents.lineSupply": "Marchandise ou prestation de services",
  "documents.goodsTreatment": "Sur ce document : {treatment}",
  "documents.intraCommunityHint":
    "Les marchandises constituent une livraison intracommunautaire exonérée. Conservez la preuve de leur arrivée dans l’autre pays de l’UE (p. ex. attestation d’arrivée ou preuve d’expédition) et déclarez la livraison dans l’état récapitulatif.",
  "documents.goodsExportHint":
    "Les marchandises constituent une livraison à l’exportation exonérée. Elles ne sont exonérées qu’avec une preuve d’exportation (document douanier) ; conservez-la.",
  "documents.lineTaxRate": "Taux de {tax}",
  "documents.lineAmount": "Montant",
  "documents.lineActions": "Actions",
  "documents.addLine": "Ajouter une position",
  "documents.addTextLine": "Ajouter une ligne de texte",
  "documents.moveUp": "Monter la position {number}",
  "documents.moveDown": "Descendre la position {number}",
  "documents.removeLine": "Retirer la position {number}",
  "documents.tooManyLines": "Un document comporte au plus {max} positions.",

  "documents.sectionConditions": "Rabais et TVA",
  "documents.discountKind": "Rabais global",
  "documents.discountPercent": "en pourcentage",
  "documents.discountAmount": "en montant en {currency}",
  "documents.discountValuePercent": "Rabais en %",
  "documents.discountValueAmount": "Rabais en {currency}",
  "documents.treatment": "Cas fiscal",
  "documents.treatmentHint":
    "Déterminé à partir du pays de l’entreprise et de celui du client. Un choix manuel est consigné.",
  "documents.treatmentAuto": "Automatique",
  "documents.treatmentAutoWith": "Automatique : {treatment}",
  "documents.overrideReason": "Justification",
  "documents.overrideReasonHint":
    "Pourquoi le cas fiscal diffère-t-il ? Enregistré dans l’historique.",
  "documents.overrideReasonRequired": "Veuillez justifier le choix manuel.",

  "documents.totals": "Totaux",
  "documents.totalsPending": "Les totaux apparaissent dès que toutes les indications sont valides.",
  "documents.subtotal": "Sous-total",
  "documents.documentDiscount": "Rabais",
  "documents.net": "Montant net",
  "documents.taxGroup": "{tax} {rate} sur {amount}",
  "documents.rounding": "Arrondi",
  "documents.total": "Total",
  "documents.paid": "Payé",
  "documents.openAmount": "En suspens",

  "documents.issue": "Émettre",
  "documents.issueTitle": "Émettre la facture ?",
  "documents.issueDescription":
    "La facture reçoit son numéro définitif et ne peut plus être modifiée ensuite. Les corrections ne sont alors possibles que par une facture d’annulation.",
  "documents.send": "Marquer comme envoyée",
  "documents.sendTitle": "Marquer l’offre comme envoyée ?",
  "documents.sendDescription":
    "L’offre reçoit son numéro et ne peut plus être modifiée ensuite. Toute modification nécessite une nouvelle offre (Dupliquer).",
  "documents.issuedToast": "Facture {number} émise.",
  "documents.sentToast": "Offre {number} marquée comme envoyée.",
  "documents.draftSaved": "Brouillon enregistré.",
  "documents.leaveConfirm":
    "Certaines modifications ne sont pas enregistrées. Quitter tout de même la page ?",
  "documents.moreActions": "Autres actions",
  "documents.duplicate": "Dupliquer",
  "documents.duplicatedToast": "Copie créée comme brouillon.",
  "documents.convert": "Convertir en facture",
  "documents.convertedToast": "Brouillon de facture créé à partir de l’offre.",
  "documents.accept": "Acceptée",
  "documents.acceptedToast": "Offre marquée comme acceptée.",
  "documents.decline": "Refusée",
  "documents.declinedToast": "Offre marquée comme refusée.",
  "documents.cancel": "Annuler la facture",
  "documents.cancelTitle": "Annuler la facture ?",
  "documents.cancelDescription":
    "Une facture d’annulation avec son propre numéro est émise et annule cette facture. Cette action est irréversible.",
  "documents.cancelledToast": "Facture annulée, facture d’annulation émise.",
  "documents.deleteDraft": "Supprimer le brouillon",
  "documents.deleteTitle": "Supprimer le brouillon ?",
  "documents.deleteDescription":
    "Le brouillon est supprimé définitivement. Les documents émis sont toujours conservés.",
  "documents.deletedToast": "Brouillon supprimé.",
  "documents.openCancellation": "Ouvrir la facture d’annulation",
  "documents.openCancelledInvoice": "Ouvrir la facture annulée",
  "documents.openSource": "Ouvrir le document d’origine",
  "documents.openInvoice": "Ouvrir le brouillon de facture",
  "documents.openConvertedInvoice": "Ouvrir la facture",
  "documents.history": "Historique",

  "documents.customerVatMissing":
    "Le client est une entreprise de l’UE, mais n’a pas d’IDE / n° TVA. Avec un numéro, on aurait : {withNumber} ; sans : {treatment}.",
  "documents.addCustomerVat": "Saisir l’IDE / n° TVA du client",
  "documents.markPaid": "Marquer comme payée",
  "documents.markedPaid": "Marquée comme payée.",
  "documents.addPayment": "Saisir un paiement",
  "documents.addPaymentDescription":
    "Montant en suspens : {amount}. Pour un paiement partiel, adaptez le montant.",
  "documents.paidOn": "Date de paiement",
  "documents.paymentAmount": "Montant en {currency}",
  "documents.paymentNote": "Note",
  "documents.recordPayment": "Enregistrer le paiement",
  "documents.paymentTooHigh": "Le montant dépasse le montant en suspens de {amount}.",
  "documents.paymentAdded": "Paiement de {amount} saisi.",
  "documents.paymentRemoved": "Paiement retiré.",
  "documents.payments": "Paiements",
  "documents.removePayment": "Retirer le paiement de {amount} du {date}",

  "writeOff.action": "Passer en perte …",
  "writeOff.title": "Passer en perte la facture {number}",
  "writeOff.description":
    "La facture reste inchangée et le client ne reçoit rien. Elle n’est plus considérée comme ouverte et ne fait plus l’objet de rappels. Si le client paie tout de même plus tard, saisissez le paiement comme d’habitude.",
  "writeOff.openAmount": "Montant en suspens",
  "writeOff.date": "Passée en perte au",
  "writeOff.today": "Aujourd’hui",
  "writeOff.endOfInvoiceYear": "Fin de l’année de facturation",
  "writeOff.dateRange": "La date se situe entre la date de la facture et aujourd’hui.",
  "writeOff.reason": "Motif",
  "writeOff.reason.gone": "Entreprise radiée ou faillite",
  "writeOff.reason.time_barred": "Prescrite",
  "writeOff.reason.small_amount": "Petit montant",
  "writeOff.reason.untraceable": "Introuvable",
  "writeOff.reason.other": "Autre motif",
  "writeOff.note": "Note",
  "writeOff.submit": "Passer en perte",
  "writeOff.done": "Facture passée en perte.",
  "writeOff.remove": "Annuler la perte",
  "writeOff.removeTitle": "Annuler la perte ?",
  "writeOff.removeDescription":
    "Le montant passé en perte redevient ouvert et peut faire l’objet de rappels. Cette opération est consignée dans l’historique.",
  "writeOff.removed": "Perte annulée.",
  "writeOff.card": "Perte sur débiteur",
  "writeOff.amount": "Passé en perte",
  "writeOff.writtenOffOn": "Au",
  "writeOff.taxIncluded": "{tax} comprise",
  "writeOff.recovered": "Payé ultérieurement",
  "writeOff.remaining": "Perte",
  "writeOff.afterWriteOff": "Reçu après la passation en perte",
  "writeOff.paymentDescription": "Passé en perte : {amount}. Le paiement réduit la perte d’autant.",
  "badDebts.description":
    "Les pertes comptent dans l’année de leur date, les paiements ultérieurs dans l’année de leur réception.",
  "badDebts.year": "Année",
  "badDebts.writtenOff": "Passé en perte",
  "badDebts.recovered": "Payé ultérieurement",
  "badDebts.loss": "Perte",
  "badDebts.net": "Net",
  "settings.tabBackup": "Sauvegarde",
  "backup.title": "Sauvegarde",
  "backup.description":
    "La base de données est sauvegardée de manière chiffrée : chaque jour et peu après chaque émission ou annulation.",
  "backup.stateTitle": "État",
  "backup.state.current": "À jour",
  "backup.state.stale": "Obsolète",
  "backup.state.not_set_up": "Non configurée",
  "backup.notSetUp":
    "Les sauvegardes ne sont pas configurées. Si le serveur tombe en panne, toutes les factures sont perdues.",
  "backup.setUpHint":
    "La destination des sauvegardes (p. ex. Dropbox, Google Drive, S3 ou un NAS) est définie lors de l’installation dans le fichier .env.",
  "backup.guide": "Ouvrir le guide",
  "backup.never": "Aucune sauvegarde réussie pour l’instant.",
  "backup.lastSuccess": "Dernière sauvegarde réussie {when}.",
  "backup.lastFailure": "La dernière tentative a échoué : {message}",
  "backup.show": "Voir les sauvegardes",
  "backup.check": "Vérifier la sauvegarde",
  "backup.now": "Sauvegarder maintenant",
  "backup.requested": "Sauvegarde demandée.",
  "backup.pending": "Demandée, démarre sous peu.",
  "backup.running": "Sauvegarde en cours …",
  "backup.unknown": "L’état des sauvegardes ne peut pas être récupéré pour le moment.",
  "backup.runs": "Dernières sauvegardes",
  "backup.startedAt": "Moment",
  "backup.reasonTitle": "Motif",
  "backup.result": "Résultat",
  "backup.added": "Nouvellement sauvegardé",
  "backup.reason.daily": "Quotidienne",
  "backup.reason.document": "Après émission ou annulation",
  "backup.reason.manual": "Manuelle",
  "backup.status.running": "En cours …",
  "backup.status.succeeded": "Réussie",
  "backup.status.failed": "Échec",
  "backup.status.not_configured": "Non configurée",
  "backup.retention":
    "Sont conservées 7 sauvegardes quotidiennes, 4 hebdomadaires, 12 mensuelles et toutes les annuelles. La restauration est décrite dans le guide.",
  "error.backup_not_set_up": "Les sauvegardes ne sont pas configurées.",
  "error.backup_folder_unusable": "Ce dossier ne peut pas être utilisé.",
  "error.backup_by_container":
    "Ici, les sauvegardes se configurent dans le conteneur de sauvegarde (voir le guide).",
  "backup.folder": "Les sauvegardes sont enregistrées dans {folder}.",
  "backup.changeFolder": "Changer de dossier",
  "backup.firstBackup":
    "La première sauvegarde démarre sous peu, dès que les données de l’entreprise sont saisies (Paramètres → Entreprise). Vous pourrez ensuite sauvegarder ici à tout moment.",
  "backup.folderUnusable": "Ce dossier ne peut pas être utilisé : {reason}",
  "backup.setup.title": "Configurer la sauvegarde",
  "backup.setup.changeTitle": "Choisir un autre dossier",
  "backup.setup.description":
    "Idéalement dans un dossier synchronisé par un service cloud. Une copie se trouve alors en dehors de cet ordinateur.",
  "backup.setup.folders": "Dossier pour les sauvegardes",
  "backup.setup.found": "trouvé",
  "backup.setup.other": "Autre dossier …",
  "backup.setup.otherHint": "Par exemple un lecteur réseau ou un disque dur USB.",
  "backup.setup.path": "Chemin du dossier",
  "backup.setup.pathHint":
    "Le chemin complet, p. ex. E:\\Rhinvoice-Backup ou /Volumes/Backup/Rhinvoice. Si le dossier n’existe pas, il est créé.",
  "backup.setup.pathMissing": "Veuillez indiquer le chemin d’un dossier.",
  "backup.setup.continue": "Continuer vers la clé",
  "backup.setup.change": "Utiliser ce dossier",
  "backup.setup.changed": "Les prochaines sauvegardes iront dans le nouveau dossier.",
  "backup.key.title": "Votre clé de restauration",
  "backup.key.description":
    "Elle chiffre les sauvegardes. Sans elle, aucune sauvegarde ne peut être restaurée. Conservez-la séparément de cet ordinateur, dans un gestionnaire de mots de passe ou imprimée.",
  "backup.key.label": "Clé de restauration",
  "backup.key.kept": "J’ai conservé la clé en lieu sûr.",
  "backup.key.finish": "Configurer la sauvegarde",
  "backup.key.done": "Sauvegarde configurée. La première démarre sous peu.",
  "backup.key.show": "Afficher la clé",
  "backup.key.hide": "Masquer la clé",
  "import.title.customers": "Importer des clients",
  "import.title.articles": "Importer des articles",
  "import.description":
    "Choisir le fichier, vérifier la correspondance, examiner le résultat. Seul « Importer » enregistre.",
  "import.chooseFile": "Choisir un fichier",
  "import.otherFile": "Autre fichier",
  "import.resultRows": "Lignes du fichier",
  "import.missingColumns": "Correspondance des colonnes : il manque une colonne pour {columns}.",
  "import.or": " ou ",
  "import.unnamedColumn": "Colonne {number}",
  "import.deleteTemplateTitle": "Supprimer le modèle ?",
  "import.deleteTemplateDescription":
    "Le modèle « {name} » est supprimé. Les données importées sont conservées.",
  "import.problem.unknown_country_prefix": "code de pays inconnu devant le NPA",
  "import.open": "Importer",
  "import.run.customers": {
    zero: "Aucun client à importer",
    one: "Importer 1 client",
    other: "Importer {count} clients",
  },
  "import.run.articles": {
    zero: "Aucun article à importer",
    one: "Importer 1 article",
    other: "Importer {count} articles",
  },
  "import.done.customers": {
    zero: "Aucun client importé.",
    one: "1 client importé.",
    other: "{count} clients importés.",
  },
  "import.done.articles": {
    zero: "Aucun article importé.",
    one: "1 article importé.",
    other: "{count} articles importés.",
  },
  "import.choose.customers": "Fichier CSV ou Excel avec des clients",
  "import.choose.articles": "Fichier CSV ou Excel avec des articles",
  "import.formats":
    "CSV (UTF-8 ou Windows-1252), Excel .xlsx et .xls, au maximum 5 Mo et {rows} lignes.",
  "import.rows": { zero: "aucune ligne", one: "1 ligne", other: "{count} lignes" },
  "import.settings": "Paramètres",
  "import.template": "Modèle",
  "import.templateHint":
    "Une correspondance des colonnes enregistrée, p. ex. pour un ancien programme.",
  "import.templateAuto": "Détecté automatiquement",
  "import.saveTemplate": "Enregistrer comme modèle",
  "import.saveTemplateHint":
    "La correspondance est enregistrée sous ce nom et s’applique aux fichiers ayant les mêmes noms de colonnes.",
  "import.templateName": "Nom du modèle",
  "import.templateSaved": "Modèle enregistré.",
  "import.templateDeleted": "Modèle supprimé.",
  "import.deleteTemplate": "Supprimer le modèle « {name} »",
  "import.duplicates": "Si l’entrée existe déjà",
  "import.duplicates.skip": "Ignorer",
  "import.duplicates.update": "Mettre à jour",
  "import.duplicates.create": "Créer une nouvelle entrée",
  "import.duplicatesHint.customers":
    "Identique signifie : même numéro de client ; sans numéro, même nom et même NPA. Lors de la mise à jour, les cellules vides restent sans effet.",
  "import.duplicatesHint.articles":
    "Identique signifie : même numéro d’article ; sans numéro, même désignation. Lors de la mise à jour, les cellules vides restent sans effet.",
  "import.mapping": "Correspondance des colonnes ({mapped} sur {total} attribuées)",
  "import.required.customers":
    "Une colonne pour le numéro de client ou le nom (entreprise ou nom de famille) est nécessaire. Les nouveaux clients nécessitent en outre le NPA et la localité.",
  "import.required.articles":
    "Une colonne pour le numéro d’article ou la désignation est nécessaire. Les nouveaux articles nécessitent une désignation.",
  "import.mappingCaption": "Correspondance des colonnes",
  "import.column": "Colonne du fichier",
  "import.example": "Exemple",
  "import.becomes": "Devient",
  "import.fieldFor": "Champ pour la colonne {column}",
  "import.skipColumn": "Ne pas importer",
  "import.field.number": "Numéro",
  "import.field.company_name": "Raison sociale",
  "import.field.salutation": "Formule d’appel",
  "import.field.first_name": "Prénom",
  "import.field.last_name": "Nom de famille",
  "import.field.contact_person": "Personne de contact",
  "import.field.street": "Rue (avec numéro)",
  "import.field.house_number": "Numéro de rue",
  "import.field.supplement": "Complément d’adresse",
  "import.field.postal_code": "NPA",
  "import.field.city": "Localité",
  "import.field.postal_city": "NPA et localité",
  "import.field.country": "Pays",
  "import.field.email": "E-mail",
  "import.field.phone": "Téléphone",
  "import.field.vat_number": "IDE / n° TVA",
  "import.field.buyer_reference": "Leitweg-ID / référence d’acheteur",
  "import.field.group": "Groupe",
  "import.field.default_currency": "Monnaie",
  "import.field.default_language": "Langue",
  "import.field.default_discount": "Rabais en %",
  "import.field.payment_terms_days": "Délai de paiement en jours",
  "import.field.tax_exempt": "Exonéré de la TVA",
  "import.field.customer_since": "Client depuis",
  "import.field.note": "Note",
  "import.field.name": "Désignation",
  "import.field.description": "Description",
  "import.field.unit": "Unité",
  "import.field.price": "Prix (monnaie de l’entreprise)",
  "import.field.price_chf": "Prix CHF",
  "import.field.price_eur": "Prix EUR",
  "import.field.tax_category": "Taux de TVA",
  "import.field.supply": "Marchandise/prestation de services",
  "import.result": "Résultat du test",
  "import.summary":
    "{new} nouveaux · {updated} mis à jour · {skipped} ignorés · {error} avec erreurs",
  "import.filter.label": "Afficher les lignes",
  "import.filter.all": "Toutes",
  "import.status.new": "Nouveau",
  "import.status.updated": "Mis à jour",
  "import.status.skipped": "Ignoré",
  "import.status.error": "Erreur",
  "import.report": "Télécharger le rapport (CSV)",
  "import.reportFile.customers": "import-clients.csv",
  "import.reportFile.articles": "import-articles.csv",
  "import.noRows": "Aucune ligne dans cette vue.",
  "import.moreRows": "Les {shown} premières lignes sur {total} ; le rapport les contient toutes.",
  "import.errorsLeftOut": "Les lignes comportant des erreurs ne sont pas importées.",
  "import.row": "Ligne",
  "import.statusTitle": "Résultat",
  "import.number": "N°",
  "import.name": "Nom",
  "import.detail.customers": "NPA et localité",
  "import.detail.articles": "Prix",
  "import.problemTitle": "Remarque",
  "import.problemText": "{field} : {reason}",
  "import.problemValue": "{field} « {value} » : {reason}",
  "import.problem.required": "manquant",
  "import.problem.invalid": "non valide",
  "import.problem.invalid_amount": "pas un montant",
  "import.problem.invalid_number": "pas un nombre entier",
  "import.problem.invalid_date": "pas une date",
  "import.problem.invalid_flag": "ni oui ni non",
  "import.problem.unknown_unit": "unité inconnue",
  "import.problem.unknown_tax_rate": "taux de TVA inconnu",
  "import.problem.unknown_supply": "ni marchandise ni prestation de services",
  "import.problem.unknown_language":
    "langue non prise en charge (allemand, anglais, français ou italien)",
  "import.problem.unknown_currency": "monnaie non prise en charge (CHF ou EUR)",
  "import.doneTitle": "Importation terminée",
  "import.show.customers": "Vers les clients",
  "import.show.articles": "Vers les articles",
  "import.again": "Importer un autre fichier",
  "import.title.invoices": "Reprendre d’anciennes factures",
  "import.demo.title": "L’importation est désactivée dans la démo",
  "import.demo.text":
    "Tous les visiteurs partagent cette démo, c’est pourquoi elle n’accepte aucun fichier. Installez Rhinvoice sur votre propre ordinateur ou serveur – avec Docker, cela ne prend que quelques minutes. Vous pourrez y reprendre clients, articles et anciennes factures avec leurs PDF.",
  "import.demo.install": "Guide d’installation",
  "import.demo.guide": "Fonctionnement de l’importation",
  "import.descriptionInvoices":
    "Factures d’un ancien programme avec leurs PDF originaux. Elles restent telles qu’elles étaient : verrouillées, avec numéro, montant et PDF.",
  "import.run.invoices": {
    zero: "Aucune facture à reprendre",
    one: "Reprendre 1 facture",
    other: "Reprendre {count} factures",
  },
  "import.done.invoices": {
    zero: "Aucune facture reprise.",
    one: "1 facture reprise.",
    other: "{count} factures reprises.",
  },
  "import.choose.invoices": "Liste des factures (CSV ou Excel)",
  "import.required.invoices":
    "Sont requis le numéro de facture, la date, le montant et le numéro ou le nom du client. Rhinvoice reconnaît la colonne des numéros grâce aux fichiers PDF.",
  "import.reportFile.invoices": "import-factures.csv",
  "import.detail.invoices": "Montant",
  "import.show.invoices": "Voir les factures",
  "import.skippedInvoices":
    "Les factures dont le numéro existe déjà sont ignorées : une facture émise ne change jamais.",
  "import.customer": "Client",
  "import.pdf": "PDF",
  "import.noPdf": "pas de PDF",
  "import.attachments": {
    zero: "aucune pièce jointe",
    one: "+ 1 pièce jointe",
    other: "+ {count} pièces jointes",
  },
  "import.newCustomer": "Client créé (archivé)",
  "import.pdfs.title": "Fichiers PDF",
  "import.pdfs.description":
    "Choisissez le dossier des factures (avec ses sous-dossiers) ou des fichiers PDF et ZIP isolés. Rhinvoice cherche le numéro de facture dans le nom du fichier ; les autres fichiers portant le même numéro (rappels, copies) sont ajoutés en pièce jointe.",
  "import.pdfs.none":
    "Aucun fichier PDF choisi pour l’instant ; les factures seront alors reprises sans PDF.",
  "import.pdfs.chosen": {
    zero: "Aucun fichier PDF dans le dossier choisi",
    one: "1 fichier PDF · {size}",
    other: "{count} fichiers PDF · {size}",
  },
  "import.pdfs.folder": "Choisir un dossier",
  "import.pdfs.files": "Choisir des fichiers",
  "import.pdfs.clear": "Retirer les fichiers PDF",
  "import.pdfs.tooMany":
    "Plus de {max} fichiers : veuillez compresser le dossier en fichier ZIP et choisir celui-ci.",
  "import.pdfs.tooLarge":
    "Plus de 200 MB au total : veuillez reprendre les fichiers en plusieurs fois.",
  "import.unused": {
    zero: "Tous les fichiers appartiennent à une facture",
    one: "1 fichier n’appartient à aucune facture de la liste",
    other: "{count} fichiers n’appartiennent à aucune facture de la liste",
  },
  "import.unusedHint":
    "Une facture manque-t-elle dans la liste, ou son numéro ne figure-t-il pas dans le nom du fichier ? Un PDF peut aussi être ajouté plus tard directement sur la facture.",
  "import.nextNumber":
    "La prochaine facture que vous établirez dans Rhinvoice recevra le numéro {number}.",
  "import.problem.non_positive_total": "montant nul ou négatif",
  "import.problem.net_above_total": "montant net supérieur au montant total",
  "import.problem.open_above_total": "montant ouvert supérieur au montant total",
  "import.field.issue_date": "Date de facture",
  "import.field.due_date": "Échéance",
  "import.field.customer_number": "Numéro de client",
  "import.field.customer_name": "Nom du client",
  "import.field.currency": "Monnaie",
  "import.field.net_amount": "Montant net",
  "import.field.tax_amount": "Montant de la TVA",
  "import.field.total_amount": "Montant total",
  "import.field.open_amount": "Montant ouvert",
  "import.field.subject": "Objet",
  "import.field.total": "Montant total",
  "import.field.net": "Montant net",
  "error.unreadable_file":
    "Le fichier ne peut pas être lu. S’agit-il d’un fichier CSV, Excel ou ZIP ?",
  "error.empty_file": "Le fichier est vide.",
  "error.file_too_large":
    "Le fichier est trop volumineux : listes 5 MB au maximum, un PDF 20 MB au maximum, tous les PDF ensemble 200 MB au maximum.",
  "error.not_a_pdf": "Ce n’est pas un fichier PDF.",
  "error.too_many_files":
    "Trop de fichiers à la fois. Veuillez les reprendre sous forme de fichier ZIP ou en plusieurs fois.",
  "error.imported_without_pdf": "Il n’existe pas encore de PDF pour cette facture reprise.",
  "error.imported_document":
    "Une facture reprise se compose de son PDF ; cette action n’est pas possible pour elle.",
  "error.not_imported": "Des fichiers ne peuvent être joints qu’aux factures reprises.",
  "error.too_many_rows":
    "Le fichier contient trop de lignes. Veuillez le diviser en fichiers de dix mille lignes au maximum.",
  "error.too_many_columns": "Le fichier contient plus de 100 colonnes.",
  "error.company_not_set_up": "Veuillez d’abord configurer l’entreprise.",
  "error.mapping_incomplete":
    "Il manque des colonnes sans lesquelles aucune ligne ne peut être importée.",
  "error.invalid_mapping": "La correspondance des colonnes n’est pas valable.",
  "error.column_used_twice": "Une colonne est attribuée à deux champs.",
  "error.unknown_column": "La correspondance mentionne une colonne qui n’existe pas.",
  "error.unknown_field": "La correspondance mentionne un champ inconnu.",
  "error.empty_template": "Le modèle n’attribue aucune colonne.",
  "wizard.title": "Configurer Rhinvoice",
  "wizard.steps": "Étapes de la configuration",
  "wizard.stepCountry": "Pays",
  "wizard.stepCompany": "Entreprise",
  "wizard.stepDone": "(terminé)",
  "paymentCode.swiss_qr_bill": "Facture QR",
  "paymentCode.epc": "GiroCode (code QR EPC)",
  "wizard.countryTitle": "Où se trouve votre entreprise ?",
  "wizard.countryHint":
    "Le pays détermine les taux de TVA, les mentions obligatoires, l’arrondi et le code QR de paiement sur la facture.",
  "wizard.smallBusinessHint": "Factures sans TVA, avec la mention prescrite.",
  "wizard.later": "Configurer plus tard",
  "wizard.next": "Suivant",
  "wizard.back": "Retour",
  "wizard.finish": "Enregistrer et continuer",
  "wizard.changeLater": "Tout peut être modifié plus tard dans les paramètres.",
  "wizard.companyTitle": "Votre entreprise",
  "wizard.companyHint": "Le nom et l’adresse figurent comme expéditeur sur chaque facture.",
  "wizard.taxNumberHint":
    "Attribué par l’administration fiscale, si vous n’avez pas de n° TVA intracommunautaire.",
  "wizard.saved": "Entreprise enregistrée. Les prochaines étapes figurent dans la vue d’ensemble.",
  "onboarding.title": "Bienvenue dans Rhinvoice",
  "onboarding.progress": "Configuration · {done} sur {total} terminées",
  "onboarding.done": "terminé",
  "onboarding.open": "ouvert",
  "onboarding.dismiss": "Masquer la liste de contrôle",
  "onboarding.dismissed": "Liste de contrôle masquée.",
  "onboarding.step.company": "Pays et entreprise",
  "onboarding.step.bank_account": "Saisir un compte bancaire",
  "onboarding.step.numbering": "Vérifier les numérotations",
  "onboarding.step.customers": "Saisir ou importer des clients",
  "onboarding.step.first_invoice": "Établir la première facture",
  "onboarding.step.backup": "Configurer la sauvegarde",
  "onboarding.hint.company": "Expéditeur, taux de TVA et mentions obligatoires des factures.",
  "onboarding.hint.bank_account": "Pour le code QR de paiement : facture QR ou GiroCode.",
  "onboarding.hint.numbering":
    "Vous venez d’un autre programme ? Le prochain numéro de facture peut y faire suite.",
  "onboarding.hint.customers": "Créer un par un ou reprendre d’un fichier CSV ou Excel.",
  "onboarding.hint.first_invoice":
    "Avec aperçu en direct ; la facture n’est émise que sur votre demande.",
  "onboarding.hint.backup": "Pour qu’aucune facture ne se perde, p. ex. vers Dropbox.",
  "onboarding.action.company": "Configurer",
  "onboarding.action.bank_account": "Saisir un compte bancaire",
  "onboarding.action.numbering": "Vérifier",
  "onboarding.action.import": "Importer",
  "onboarding.action.customers": "Créer un client",
  "onboarding.action.first_invoice": "Établir une facture",
  "onboarding.action.backup": "Voir la sauvegarde",
  "settings.tabPrint": "Mise en page d’impression",
  "printLayout.title": "Mise en page d’impression",
  "printLayout.description":
    "L’apparence des factures, offres et annulations sur papier et en PDF.",
  "printLayout.template": "Modèle",
  "printLayout.template.classic": "Classique",
  "printLayout.template.modern": "Moderne",
  "printLayout.template.plain": "Sobre",
  "printLayout.templateHint.classic": "Ligne sous l’en-tête, en-tête de tableau gris",
  "printLayout.templateHint.modern": "Couleur de l’entreprise en accent, total mis en évidence",
  "printLayout.templateHint.plain": "Beaucoup d’espace blanc, sans couleur",
  "printLayout.logoAndFont": "Logo et police",
  "printLayout.logoPosition": "Logo",
  "printLayout.logoPosition.left": "À gauche",
  "printLayout.logoPosition.right": "À droite",
  "printLayout.logoSize": "Taille du logo",
  "printLayout.logoSize.small": "Petit",
  "printLayout.logoSize.medium": "Moyen",
  "printLayout.logoSize.large": "Grand",
  "printLayout.font": "Police",
  "printLayout.font.sans": "Sans empattement (Inter)",
  "printLayout.font.serif": "Avec empattement (Source Serif)",
  "printLayout.colourHint":
    "Le logo et la couleur de l’entreprise se définissent sous Paramètres → Entreprise.",
  "printLayout.content": "Contenu",
  "printLayout.senderLine": "Ligne d’expéditeur au-dessus de l’adresse",
  "printLayout.senderLineHint": "La petite ligne dans la fenêtre d’adresse.",
  "printLayout.contact": "Coordonnées dans l’en-tête",
  "printLayout.contactHint": "Téléphone, e-mail et site web sous l’adresse en haut à droite.",
  "printLayout.positionNumbers": "Numéros de position",
  "printLayout.positionNumbersHint": "La colonne « Pos. » du tableau.",
  "printLayout.footer": "Pied de page",
  "printLayout.footerAccounts": "Coordonnées bancaires",
  "printLayout.footerAccounts.all": "Tous les comptes",
  "printLayout.footerAccounts.invoice_currency": "Seulement la monnaie de la facture",
  "printLayout.footerAccounts.none": "Aucune",
  "printLayout.footerHint":
    "Les comptes d’une même banque sont regroupés. Les comptes de tiers n’apparaissent que sur les documents payés à ceux-ci. Le code QR de paiement utilise toujours le compte sur lequel la facture est payée.",
  "printLayout.reset": "Réinitialiser",
  "printLayout.issuedStay": "S’applique aux nouveaux PDF. Les documents émis conservent leur PDF.",
  "printLayout.preview": "Aperçu de la mise en page d’impression",
  "printLayout.previewHint": "Aperçu avec la facture la plus récente.",
  "settings.tabDunning": "Relances",
  "dunning.title": "Relances",
  "dunning.description": "Factures en retard, rappels déjà établis et prochaine étape.",
  "dunning.settings": "Configurer les niveaux de relance",
  "dunning.emptyTitle": "Rien en retard",
  "dunning.emptyDescription": "Dès qu’une facture dépasse sa date d’échéance, elle apparaît ici.",
  "dunning.table": "Factures en retard",
  "dunning.invoice": "Facture",
  "dunning.customer": "Client",
  "dunning.open": "Ouvert",
  "dunning.overdue": "En retard",
  "dunning.reminders": "Établis",
  "dunning.next": "Prochaine étape",
  "dunning.days": {
    zero: "Depuis aujourd’hui",
    one: "Depuis 1 jour",
    other: "Depuis {count} jours",
  },
  "dunning.none": "Aucun pour l’instant",
  "dunning.level.1": "Rappel de paiement",
  "dunning.level.2": "1er rappel",
  "dunning.level.3": "2e rappel",
  "dunning.done":
    "Tous les niveaux sont établis. Les étapes suivantes (p. ex. recouvrement) se font en dehors de Rhinvoice.",
  "dunning.write": "Établir : {level}",
  "dunning.written": "{level} établi(e).",
  "dunning.openPdf": "Ouvrir le PDF",
  "dunning.dueSince": "Échue depuis le {date}",
  "dunning.dueFrom": "Échue à partir du {date}",
  "dunning.confirmTitle": "Établir « {level} » pour {number} ?",
  "dunning.confirmEarly": "Ce niveau n’est échu qu’à partir du {date}.",
  "dunning.confirmDetails":
    "À {customer}, nouveau délai {deadline}, frais de rappel {fee}. Le rappel reste dans l’historique de la facture.",
  "dunning.problems": "Veuillez corriger les champs signalés.",
  "dunning.problem.days": "Nombre entier de 0 à 365",
  "dunning.problem.paymentDays": "Nombre entier de 1 à 365",
  "dunning.problem.fee": "Montant jusqu’à 1000 par pas de 0.05",
  "dunning.problem.text": "2000 caractères au maximum",
  "eInvoice.beforeEInvoices":
    "Le PDF a été créé avant que Rhinvoice n’intègre les factures électroniques.",
  "eInvoice.issueWarning": "Cette facture sera émise sans facture électronique. Il manque :",
  "eInvoice.issueXRechnungWarning": "Il manque pour la XRechnung (autorités allemandes) :",
  "eInvoice.xrechnungSummary": "XRechnung pour les autorités allemandes",
  "eInvoice.title": "Facture électronique",
  "eInvoice.embedded":
    "Le PDF contient la facture électronique (ZUGFeRD, EN 16931) : les logiciels de comptabilité lisent directement les données.",
  "eInvoice.pending":
    "La facture électronique est intégrée lors de la création du PDF (ZUGFeRD, EN 16931).",
  "eInvoice.notEmbedded": "Le PDF archivé ne contient pas de facture électronique.",
  "eInvoice.disabled":
    "Lors de l’émission, la facture électronique était désactivée dans la mise en page d’impression.",
  "eInvoice.missing": "Il manquait lors de l’émission, pour une facture électronique :",
  "eInvoice.xrechnung": "XRechnung (XML)",
  "eInvoice.xrechnungHint":
    "Pour les autorités allemandes ; la Leitweg-ID vient du client ou de la référence de la facture.",
  "eInvoice.xrechnungMissing": "Il manquait lors de l’émission, pour une XRechnung :",
  "eInvoice.nextTime":
    "Les factures émises restent inchangées. Complétez les indications pour les factures futures.",
  "eInvoice.problem.seller_id_missing":
    "IDE, n° TVA intracommunautaire ou inscription au registre du commerce (Paramètres → Entreprise)",
  "eInvoice.problem.seller_vat_id_missing":
    "N° TVA ou n° TVA intracommunautaire (Paramètres → Entreprise)",
  "eInvoice.problem.seller_tax_id_missing":
    "N° TVA, n° TVA intracommunautaire ou numéro fiscal (Paramètres → Entreprise)",
  "eInvoice.problem.buyer_vat_id_missing": "IDE ou n° TVA avec code pays chez le client",
  "eInvoice.problem.buyer_reference_missing": "Leitweg-ID du client ou référence de la facture",
  "eInvoice.problem.seller_email_missing": "E-mail (Paramètres → Entreprise)",
  "eInvoice.problem.seller_phone_missing": "Téléphone (Paramètres → Entreprise)",
  "eInvoice.problem.buyer_email_missing": "E-mail chez le client",
  "eInvoice.problem.bank_account_missing": "Compte bancaire (Paramètres → Comptes bancaires)",
  "printLayout.eInvoiceTitle": "Facture électronique",
  "printLayout.eInvoice": "Facture électronique dans le PDF (ZUGFeRD)",
  "printLayout.eInvoiceHint":
    "Les factures et factures d’annulation émises à partir de maintenant contiennent en plus leurs données sous forme lisible par machine (EN 16931). Le PDF garde le même aspect.",
  "error.e_invoice_incomplete": "Il manque des indications pour une XRechnung.",
  "error.not_an_e_invoice":
    "Seules les factures et factures d’annulation émises sont des factures électroniques.",
  "documents.reminders": "Rappels",
  "dunning.settingsTitle": "Niveaux de relance",
  "dunning.settingsDescription":
    "Trois niveaux : rappel de paiement, 1er et 2e rappel. Chaque niveau devient échu après un nombre de jours et fixe un nouveau délai de paiement.",
  "dunning.daysAfterInvoice": "Jours après l’échéance",
  "dunning.daysAfterReminder": "Jours après l’expiration du dernier délai",
  "dunning.paymentDays": "Nouveau délai de paiement en jours",
  "dunning.fee": "Frais de rappel",
  "dunning.feeHint":
    "Dans la monnaie de la facture, hors TVA. Uniquement en cas d’accord ou de frais justifiés ; 0 pour aucun.",
  "dunning.textDe": "Texte (allemand)",
  "dunning.textEn": "Texte (anglais)",
  "dunning.textFr": "Texte (français)",
  "dunning.textIt": "Texte (italien)",
  "dunning.textHint":
    "Variables : {faellig} (nouveau délai), {betrag}, {waehrung}, {nummer}, {kunde}.",
  "error.not_remindable": "Cette facture n’est pas en retard ou a déjà reçu tous les rappels.",
  "error.levels_incomplete": "Les trois niveaux de relance sont requis.",
  "recurring.short": "Récurrente",
  "recurring.title": "Factures récurrentes",
  "recurring.description":
    "Factures que Rhinvoice établit automatiquement chaque mois, chaque trimestre ou chaque année.",
  "recurring.new": "Nouveau plan",
  "recurring.newDescription": "La facture ci-dessous est recréée à chaque échéance.",
  "recurring.emptyTitle": "Aucune facture récurrente pour l’instant",
  "recurring.emptyDescription":
    "Pour l’hébergement, la maintenance, les abonnements ou les forfaits : créez la facture une fois, Rhinvoice l’établit à nouveau à chaque échéance.",
  "recurring.name": "Désignation",
  "recurring.nameHint": "Uniquement pour vous, p. ex. « Hébergement Holzwerk ».",
  "recurring.customer": "Client",
  "recurring.interval": "Intervalle",
  "recurring.intervalOption.monthly": "Mensuel",
  "recurring.intervalOption.quarterly": "Trimestriel",
  "recurring.intervalOption.half_yearly": "Semestriel",
  "recurring.intervalOption.yearly": "Annuel",
  "recurring.nextDate": "Prochaine facture",
  "recurring.amount": "Montant",
  "recurring.action": "À l’échéance",
  "recurring.actionShort.draft": "Brouillon",
  "recurring.actionShort.issue": "Émettre",
  "recurring.actionShort.send": "Émettre et envoyer",
  "recurring.actionOption.draft": "Créer un brouillon",
  "recurring.actionOption.issue": "Émettre",
  "recurring.actionOption.send": "Émettre et envoyer par e-mail",
  "recurring.actionHint.draft":
    "Le brouillon attend sous Factures jusqu’à ce que vous le vérifiiez et l’émettiez.",
  "recurring.actionHint.issue": "La facture est émise avec un numéro ; vous l’envoyez vous-même.",
  "recurring.actionHint.send": "La facture est émise et envoyée à l’adresse e-mail du client.",
  "recurring.state": "Statut",
  "recurring.stateLabel.active": "Actif",
  "recurring.stateLabel.paused": "En pause",
  "recurring.stateLabel.finished": "Terminé",
  "recurring.stateLabel.problem": "Problème",
  "recurring.create": "Créer le plan",
  "recurring.created": "Plan créé.",
  "recurring.planTitle": "Répétition",
  "recurring.planDescription": "Quand la facture est établie et ce qui se passe ensuite.",
  "recurring.startDate": "Première facture le",
  "recurring.startDateHint":
    "Chaque facture suivante suit l’intervalle à partir de ce jour (le 31 reste la fin du mois).",
  "recurring.endDate": "Dernière facture jusqu’au",
  "recurring.endDateHint": "Laisser vide jusqu’à ce que vous mettiez fin au plan.",
  "recurring.onTheDay": "À l’échéance",
  "recurring.mailNotConfigured": "L’envoi d’e-mails n’est pas encore configuré.",
  "recurring.servicePeriod": "Indiquer la période de prestation (p. ex. 01.10.–31.10.)",
  "recurring.moreHint": "Objet, référence, monnaie, langue, rabais",
  "recurring.field.name": "Désignation",
  "recurring.field.startDate": "Première facture le",
  "recurring.field.endDate": "Dernière facture jusqu’au",
  "recurring.problem.required": "Veuillez remplir ce champ.",
  "recurring.problem.endBeforeStart": "La fin est antérieure à la première facture.",
  "recurring.statusTitle": "État",
  "recurring.finished": "Le plan est terminé : aucune autre facture ne suivra.",
  "recurring.next": "Prochaine facture le {date}.",
  "recurring.nextWithPeriod": "Prochaine facture le {date}, période de prestation jusqu’au {to}.",
  "recurring.customerArchived":
    "Le client est archivé, mais le plan continue à le facturer. Mettez le plan en pause si ce n’est plus correct.",
  "recurring.overriddenSource":
    "La facture avait un cas fiscal choisi manuellement. Les factures récurrentes le déterminent automatiquement ; vérifiez l’aperçu.",
  "recurring.pausedHint": "En pause : les échéances pendant la pause sont supprimées.",
  "recurring.noRuns": "Aucune facture établie pour l’instant.",
  "recurring.runs": "Factures établies",
  "recurring.outcome.draft": "Brouillon",
  "recurring.outcome.issued": "Émise",
  "recurring.outcome.sent": "Envoyée",
  "recurring.outcome.failed": "Échec",
  "recurring.openInvoice": "Ouvrir la facture",
  "recurring.customerEmailMissing":
    "Non envoyée : aucune adresse e-mail n’est enregistrée pour le client.",
  "recurring.interrupted": "Interrompu avant que la facture ne soit établie.",
  "recurring.interruptedAfterCreate":
    "Interrompu après la création de la facture. Veuillez vérifier la facture.",
  "recurring.pause": "Mettre en pause",
  "recurring.resume": "Reprendre",
  "recurring.paused": "Plan mis en pause.",
  "recurring.resumed": "Plan repris.",
  "recurring.delete": "Supprimer le plan",
  "recurring.deleteTitle": "Supprimer le plan « {name} » ?",
  "recurring.deleteDescription":
    "Aucune autre facture ne sera établie. Les factures déjà établies sont conservées.",
  "recurring.deleted": "Plan « {name} » supprimé.",
  "recurring.makeRecurring": "Rendre récurrente",
  "error.plan_start_in_past":
    "La première facture ne peut pas se situer dans le passé. Un nouvel intervalle nécessite un nouveau premier jour (aujourd’hui ou plus tard).",
  "error.plan_not_paused": "Le plan n’est pas en pause.",
  "mail.sendAction": "Envoyer par e-mail",
  "mail.sendTitle": "Envoyer {title} par e-mail",
  "mail.sendDescription":
    "Vérifiez les destinataires et le texte. Le PDF est joint et l’envoi est consigné dans l’historique.",
  "mail.notConfigured":
    "L’envoi d’e-mails n’est pas encore configuré. Pour cela, indiquez une fois le serveur de messagerie de votre fournisseur e-mail.",
  "mail.setUp": "Configurer l’e-mail",
  "mail.to": "À",
  "mail.toHint": "Séparer plusieurs adresses par une virgule.",
  "mail.noCustomerAddress":
    "Aucune adresse e-mail n’est enregistrée pour le client. Séparer plusieurs adresses par une virgule.",
  "mail.subject": "Objet",
  "mail.body": "Message",
  "mail.attachments": "Pièce jointe",
  "mail.attachXrechnung": "Joindre aussi la XRechnung (XML)",
  "mail.copyTo": "Une copie est envoyée à {address}.",
  "mail.send": "Envoyer",
  "mail.checkFields": "Veuillez vérifier les indications signalées.",
  "mail.cancelledWarning":
    "Cette facture est annulée. Ne l’envoyez que si le client en a de nouveau besoin.",
  "mail.sending": "Envoi en cours …",
  "mail.sent": "E-mail envoyé à {to}.",
  "mail.recipientMissing": "Veuillez indiquer au moins une adresse e-mail.",
  "mail.invalidRecipient": "« {address} » n’est pas une adresse e-mail valable.",
  "mail.tooManyRecipients": "{max} destinataires au maximum.",
  "mail.serverTitle": "Serveur de messagerie",
  "mail.serverDescription":
    "Rhinvoice envoie via le serveur sortant (SMTP) de votre fournisseur e-mail. Vous trouverez les indications dans son aide, p. ex. Microsoft 365 : smtp.office365.com, port 587, STARTTLS. Gmail et iCloud exigent un mot de passe d’application.",
  "mail.problems": "Veuillez vérifier les indications signalées.",
  "mail.host": "Serveur",
  "mail.hostHint": "Par exemple smtp.votre-fournisseur.ch",
  "mail.port": "Port",
  "mail.security": "Chiffrement",
  "mail.securityOption.starttls": "STARTTLS (généralement port 587)",
  "mail.securityOption.tls": "SSL/TLS (généralement port 465)",
  "mail.securityOption.none": "Aucun",
  "mail.securityHint.starttls": "Recommandé : la connexion est chiffrée après son établissement.",
  "mail.securityHint.tls": "La connexion est chiffrée dès le début.",
  "mail.securityHint.none":
    "Non chiffré. À utiliser uniquement pour un serveur de messagerie dans votre propre réseau.",
  "mail.username": "Nom d’utilisateur",
  "mail.usernameHint":
    "Généralement l’adresse e-mail. Laisser vide si le serveur ne demande pas de connexion.",
  "mail.password": "Mot de passe",
  "mail.passwordHint": "Enregistré et plus jamais affiché.",
  "mail.passwordKept": "Un mot de passe est enregistré. Laisser vide pour le conserver.",
  "mail.sender": "Adresse d’expéditeur",
  "mail.senderHint": "De nombreux fournisseurs n’envoient que depuis leurs propres adresses.",
  "mail.senderName": "Nom d’expéditeur",
  "mail.replyTo": "Adresse de réponse",
  "mail.replyToHint": "Les réponses des clients arrivent à cette adresse au lieu de l’expéditeur.",
  "mail.copyToSender": "Envoyer une copie de chaque e-mail à l’adresse d’expéditeur",
  "mail.problem.required": "Veuillez remplir ce champ.",
  "mail.problem.port": "Un nombre entre 1 et 65535.",
  "mail.problem.email": "Adresse e-mail non valable.",
  "mail.problem.passwordWithoutUser": "Un mot de passe nécessite un nom d’utilisateur.",
  "mail.testTitle": "E-mail de test",
  "mail.testDescription": "Vérifiez les indications enregistrées avec un court e-mail.",
  "mail.testFirstSave": "Enregistrez d’abord le serveur de messagerie.",
  "mail.testTo": "Envoyer à",
  "mail.sendTest": "Envoyer un e-mail de test",
  "mail.testSent": "E-mail de test envoyé à {to}. Veuillez vérifier la boîte de réception.",
  "mail.templatesTitle": "Modèles d’e-mail",
  "mail.templatesDescription":
    "Objet et texte des e-mails avec lesquels les documents et les rappels sont envoyés. Les variables sont remplies lors de l’envoi.",
  "mail.templateKind": "Pour",
  "mail.templateLanguage": "Langue",
  "mail.templateOwn": "Modèle personnalisé.",
  "mail.templateBuiltIn": "Modèle fourni.",
  "mail.restoreTemplate": "Utiliser le modèle fourni",
  "mail.templateRestored": "Modèle fourni rétabli.",
  "mail.kind.invoice": "Facture",
  "mail.kind.quote": "Offre",
  "mail.kind.cancellation": "Facture d’annulation",
  "mail.kind.reminder_1": "Rappel de paiement",
  "mail.kind.reminder_2": "1er rappel",
  "mail.kind.reminder_3": "2e rappel",
  "mail.reminderSent": "{level} envoyé(e) par e-mail",

  "export.open": "Exporter",
  "export.title": "Exporter les factures",
  "export.description":
    "Les factures et notes de crédit émises d’une période selon la date de facture, pour votre comptabilité ou votre fiduciaire.",
  "export.period": "Période",
  "export.period.month": "Mois",
  "export.period.quarter": "Trimestre",
  "export.period.year": "Année",
  "export.period.custom": "Libre",
  "export.month": "Mois",
  "export.quarter": "Trimestre",
  "export.quarterName": "{quarter}e trimestre",
  "export.year": "Année",
  "export.from": "Du",
  "export.to": "Au",
  "export.problem.missing": "Veuillez indiquer les deux jours.",
  "export.problem.reversed": "La fin précède le début.",
  "export.problem.tooLong": "Dix ans au plus à la fois.",
  "export.format": "Format",
  "export.format.xlsx": "Excel",
  "export.format.csv": "CSV",
  "export.format.pdf": "PDF",
  "export.withPdfs": "Joindre les PDF des factures (ZIP)",
  "export.withPdfsHint":
    "Un fichier ZIP avec la liste et le PDF de chaque facture ; la colonne « Fichier » en donne le nom.",
  "export.download": "Télécharger",
  "export.preparing": "Préparation …",
  "error.period_reversed": "La période se termine avant de commencer.",
  "error.period_too_long": "Une période couvre dix ans au plus.",
  "error.export_busy":
    "Un autre export est en cours de préparation. Veuillez réessayer dans un instant.",
};
